<template>
  <div class="wrapper flex justify-center align-items-center" v-if="loading">
    <div class="progress-container">
      <p class="q-mb-md">
        <img
          v-if="isBobbieContext"
          class="airteam-logo"
          src="@/assets/model/bobbie_logo.svg"
        />
        <img
          v-else
          class="airteam-logo"
          src="@/assets/model/airteam_transparent.svg"
        />
        <span v-if="!isBobbieContext">AIRTEAM</span>
      </p>
      <q-linear-progress
        :value="loadingProgress"
        size="md"
        color="blue"
        rounded
        track-color="white"
      />
      <p class="progress-hint q-mt-md">{{ $t("canvas.loadingModel") }}</p>
    </div>
  </div>
  <div
    v-show="!loading"
    class="viewer-wrapper row"
    :class="{ 'light-mode': isLightMode }"
  >
    <div
      v-if="hasPermission(Permission.View_Tools)"
      v-show="!sidebarOpen"
      class="col-p5"
    >
      <span class="cursor-pointer open-sidebar" @click="toggleSidebar($event)">
        <div>
          <img src="@/assets/icons/chevron-right.svg" />
        </div>
      </span>
    </div>

    <div
      v-if="!isFrontendOnlyDemo && hasPermission(Permission.View_Tools)"
      v-show="sidebarOpen"
      ref="leftPanelRef"
      :class="[
        addonEnabled('airteamDesignerEnabled') && aiPanelOpen
          ? 'col-lg-4 col-md-5 col-sm-6'
          : 'col-lg-2 col-md-3 col-sm-4',
        'col-xs-12 project-panel q-pa-lg new-sidebar-layout',
      ]"
      style="padding: 0"
      @click="closeEditMode"
    >
      <BreadCrumbs
        :projectId="$route.params.projectId || project.id"
        :projectName="project.name"
        :isSample="project.isSample"
      />

      <div class="sidebar-top q-pa-lg">
        <div v-if="!isFrontendOnlyDemo" class="project-panel__title">
          <p class="project-panel__title-value">
            <LocationIcon
              style="margin-bottom: 5px"
              class="q-mr-sm"
              :color="activeColor"
            />
            {{ project.isSample ? project.description : project.address }}
          </p>
          <span
            v-show="!fullScreenImageActive && !csvVisualizerVisible"
            class="cursor-pointer close-sidebar"
            @click="toggleSidebar($event)"
          >
            <div>
              <img src="@/assets/icons/chevron-left.svg" />
            </div>
          </span>
        </div>
        <p v-else class="project-panel__id">
          {{ $t("sidebar.sampleProject") }}
        </p>
      </div>
      <div
        class="sidebar-bottom"
        :class="{
          'sidebar-bottom--iframe': isInIFrameContext,
          'sidebar-bottom--ai':
            addonEnabled('airteamDesignerEnabled') && aiPanelOpen,
        }"
        style="padding: 2px 24px 0 24px"
      >
        <template
          v-if="
            addonEnabled('airteamDesignerEnabled') &&
            aiPanelOpen &&
            !anonymousUser
          "
        >
          <AiDesignComponent
            class="ai-design-section"
            :sendFn="sendAiInstruction"
            :has2dPreview="!!aiPreviewImage"
            @backTo3d="clearAiPreview"
          />
        </template>
        <template v-else>
          <q-expansion-item
            v-model="summaryExpanded"
            :header-class="`summary__header ${
              summaryExpanded ? 'summary__header--expanded' : ''
            }`"
            class="summary__block"
            :label="$t('sidebar.summary')"
            :caption="$t('canvas.unitOfMeasurement')"
            :expand-separator="false"
          >
            <q-card
              class="summary__card"
              :class="summaryExpanded && 'summary__card--expanded'"
            >
              <q-card-section>
                <div class="summary__content">
                  <p class="project-panel__field q-mb-sm">
                    <span class="project-panel__field--label">{{
                      $t("sidebar.totalRoofArea")
                    }}</span>
                    <span class="project-panel__field--value"
                      >{{ totalRoofArea }}m²</span
                    >
                  </p>
                  <p class="project-panel__field q-mb-sm">
                    <span class="project-panel__field--label">{{
                      $t("sidebar.numberOfPanels")
                    }}</span>
                    <span class="project-panel__field--value">{{
                      totalNumberOfPanels
                    }}</span>
                  </p>
                  <p class="project-panel__field q-mb-sm">
                    <span class="project-panel__field--label">{{
                      $t("sidebar.totalPanelsArea")
                    }}</span>
                    <span class="project-panel__field--value"
                      >{{ totalPanelsArea }}m²</span
                    >
                  </p>
                </div>
              </q-card-section>
            </q-card>
          </q-expansion-item>
          <q-expansion-item
            v-if="currentUser.isEmployee"
            v-model="orientationCardExpanded"
            :header-class="`summary__header summary__header--small ${
              orientationCardExpanded ? 'summary__header--expanded' : ''
            }`"
            class="summary__block"
            :label="$t('sidebar.modelOrientation')"
          >
            <q-card
              class="summary__card"
              :class="orientationCardExpanded && 'summary__card--expanded'"
            >
              <q-card-section>
                <div class="orientation-input-group q-mb-md">
                  <p class="orientation-label">{{ $t("sidebar.xAxis") }}</p>
                  <el-input-number
                    :model-value="project.orientation.x"
                    type="number"
                    class="orientation-input-field"
                    controls-position="right"
                    :precision="2"
                    :min="-180"
                    :max="180"
                    :value-on-clear="null"
                    @update:modelValue="
                      (value) => {
                        project.orientation.x = value;
                        orientationUpdated();
                      }
                    "
                  >
                    <template #increase-icon>
                      <img src="@/assets/icons/arrow-up.svg" />
                    </template>
                    <template #decrease-icon>
                      <img src="@/assets/icons/arrow-down.svg" />
                    </template>
                  </el-input-number>
                </div>

                <div class="orientation-input-group q-mb-md">
                  <p class="orientation-label">{{ $t("sidebar.yAxis") }}</p>
                  <el-input-number
                    :model-value="project.orientation.y"
                    type="number"
                    class="orientation-input-field"
                    controls-position="right"
                    :precision="2"
                    :min="-180"
                    :max="180"
                    :value-on-clear="null"
                    @update:modelValue="
                      (value) => {
                        project.orientation.y = value;
                        orientationUpdated();
                      }
                    "
                  >
                    <template #increase-icon>
                      <img src="@/assets/icons/arrow-up.svg" />
                    </template>
                    <template #decrease-icon>
                      <img src="@/assets/icons/arrow-down.svg" />
                    </template>
                  </el-input-number>
                </div>

                <div class="orientation-input-group q-mb-md">
                  <p class="orientation-label">{{ $t("sidebar.zAxis") }}</p>
                  <el-input-number
                    :model-value="project.orientation.z"
                    type="number"
                    class="orientation-input-field"
                    controls-position="right"
                    :precision="2"
                    :min="-180"
                    :max="180"
                    :value-on-clear="null"
                    @update:modelValue="
                      (value) => {
                        project.orientation.z = value;
                        orientationUpdated();
                      }
                    "
                  >
                    <template #increase-icon>
                      <img src="@/assets/icons/arrow-up.svg" />
                    </template>
                    <template #decrease-icon>
                      <img src="@/assets/icons/arrow-down.svg" />
                    </template>
                  </el-input-number>
                </div>

                <q-btn
                  no-caps
                  color="primary"
                  class="add-panels q-mt-sm"
                  @click="saveOrientation"
                  >{{ $t("sidebar.save") }}
                </q-btn>

                <div class="q-mt-sm">
                  <span
                    class="reset-orientation-link"
                    @click="resetOrientation"
                  >
                    {{ $t("sidebar.resetAll") }}
                  </span>
                </div>
              </q-card-section>
            </q-card>
          </q-expansion-item>
          <q-expansion-item
            v-if="currentUser.isEmployee"
            v-model="scaleCardExpanded"
            :header-class="`summary__header summary__header--small ${
              scaleCardExpanded ? 'summary__header--expanded' : ''
            }`"
            class="summary__block"
            :label="$t('sidebar.scaleModel')"
            style="color: black"
          >
            <q-card
              class="summary__card"
              :class="scaleCardExpanded && 'summary__card--expanded'"
            >
              <q-card-section>
                <div>
                  <input
                    type="number"
                    class="scale-input-field"
                    :value="project.scale"
                    step="0.1"
                    min="0.1"
                    max="10"
                    @input="
                      (event) => {
                        scalingUpdated(parseFloat(event.target.value));
                      }
                    "
                  />
                </div>
              </q-card-section>
            </q-card>
          </q-expansion-item>

          <hr class="project-panel__line project-panel__line--tight" />

          <div v-if="currentUser.isEmployee" class="fps-toggle">
            <ToggleSwitch v-model="showFps" :label="$t('sidebar.toggleFps')" />
          </div>

          <MainSidebar
            v-if="project.id && !anonymousUser"
            ref="mainSidebaRef"
            :solarGroupDetails="areas"
            :distancesDetails="measurements"
            :areaDetails="measurementAreas"
            :completedAreas="completedAreas"
            :hideAreas="hideAreas"
            :showAreas="showAreas"
            :measurementAreaCreated="measurementAreaCreated"
            :showSingleArea="showSingleArea"
            :hideSingleArea="hideSingleArea"
            :hideAllMeasurementAreas="hideAllMeasurementAreas"
            :showAllMeasurementAreas="showAllMeasurementAreas"
            :hideMeasurements="hideMeasurements"
            :showMeasurements="showMeasurements"
            @updateSelectedArea="updateSelectedArea"
            @updateSelectedDistance="updateSelectedDistance"
            :projectId="project.id"
            :objectIsloaded="objectIsloaded"
            :deleteMeasurement="deleteMeasurement"
            :selectedFromSidebarEvent="selectedFromSidebarEvent"
            :removeSelectedMeasurementArea="removeSelectedMeasurementArea"
            :removeSolarGroup="removeSolarGroup"
            :detectedMeasurementArea="detectedMeasurementArea"
            :unselectFromSidebar="unselectFromSidebar"
            :unselectSolarArea="unselectSolarArea"
            :anonymousUser="anonymousUser"
            :active="active"
            @updateActive="updateActive"
            :selectedMeasurementArea="selectedMeasurementArea"
            :unselectMeasurementArea="unselectMeasurementArea"
            :getAreaPanelsCount="getAreaPanelsCount"
            :calculateVisiblePanelsArea="calculateVisiblePanelsArea"
            :calculateSurfaceArea="calculateSurfaceArea"
            :removeSelectedArea="removeSelectedArea"
            :fromDraggStart="fromDraggStart"
            :annotations="allAnnotations"
            :selectedAnnotationId="selectedAnnotationId"
            :hideAnnotationMarkers="hideAnnotationMarkers"
            :showAnnotationMarkers="showAnnotationMarkers"
            @selectAnnotation="handleSelectAnnotation"
          />
          <hr v-if="currentUser.isEmployee" class="project-panel__line" />
          <hr v-if="currentUser.isEmployee" class="project-panel__line" />
          <button
            v-if="showExportCta"
            class="export-cta"
            @click="openExportModal"
          >
            <UploadIcon />
            {{ $t("exportPanel.cta") }}
          </button>
          <ExportModal
            v-if="showExportCta"
            v-model:open="exportModalOpen"
            :items="exportItems"
            :project-id="project.id"
          />
        </template>
      </div>
    </div>
    <div
      id="airteam-viewer"
      ref="airteamViewerDiv"
      class="airteam-viewer"
      :class="viewerColumnClass"
    >
      <img
        v-if="addonEnabled('airteamDesignerEnabled') && aiPreviewImage"
        :src="aiPreviewImage"
        class="ai-preview-image"
        alt="AI-generated building preview"
      />
      <FirstVisitHint v-if="showFirstVisitHint" />
      <div
        v-if="hasPermission(Permission.View_Tools)"
        v-show="active !== 2 && active !== 3 && active !== 6"
        class="position0 q-pa-sm q-ml-sm hide-mobile"
      >
        <div
          class="undo-redo q-mx-sm"
          :class="undoStack.length === 0 ? 'cursor-disabled' : 'cursor-pointer'"
          @click="undo"
        >
          <img
            v-show="!undoLoading"
            class="undo-arrow"
            src="@/assets/icons/left-arrow.svg"
          />
          <img
            v-show="undoLoading"
            class="undo-arrow"
            src="@/assets/icons/spinner-loading.svg"
          />
        </div>
        <div
          class="undo-redo q-mx-sm"
          :class="redoStack.length === 0 ? 'cursor-disabled' : 'cursor-pointer'"
          @click="redo"
        >
          <img
            v-show="!redoLoading"
            class="undo-arrow"
            src="@/assets/icons/right-arrow.svg"
          />
          <img
            v-show="redoLoading"
            class="undo-arrow"
            src="@/assets/icons/spinner-loading.svg"
          />
        </div>
      </div>
      <div class="position1" id="right-side-bar">
        <div
          v-if="!isFrontendOnlyDemo || showThemeToggle"
          class="screenshot-bg"
        >
          <button
            v-if="showThemeToggle"
            type="button"
            class="action-tile cursor-pointer hide-mobile"
            :aria-label="
              isLightMode
                ? $t('actionBar.darkBackground')
                : $t('actionBar.lightBackground')
            "
            :aria-pressed="isLightMode"
            @click="toggleViewerTheme(!isLightMode)"
          >
            <ThemeSwitchIcon :light-mode="isLightMode" />
            <!-- Names the background a click switches to, as the camera
                 toggle does for its view. -->
            <q-tooltip>{{
              isLightMode
                ? $t("actionBar.darkBackground")
                : $t("actionBar.lightBackground")
            }}</q-tooltip>
          </button>
          <button
            v-if="!isFrontendOnlyDemo && !anonymousUser"
            type="button"
            class="action-tile hide-mobile"
            :class="active !== 0 ? 'cursor-disabled' : 'cursor-pointer'"
            :aria-label="$t('actionBar.screenshot')"
            @click="takeScreenshot"
          >
            <ScreenshotActionIcon />
            <q-tooltip>{{ $t("actionBar.screenshot") }}</q-tooltip>
          </button>
          <button
            v-if="!isFrontendOnlyDemo && !anonymousUser"
            type="button"
            class="action-tile cursor-pointer"
            :aria-label="$t('actionBar.share')"
            @click="shareSolarModel"
          >
            <ShareActionIcon />
            <q-tooltip>{{ $t("actionBar.share") }}</q-tooltip>
          </button>
        </div>

        <SolarSidebar
          v-if="selectedArea && selectedArea.type === 'SOLAR_GROUP'"
          ref="solarSidebar"
          :areas="areas"
          :horizontalPanels="horizontalPanelTypes"
          :verticalPanels="verticalPanelTypes"
          :activeUserId="currentUser.id"
          :selectedSolarGroup="selectedArea"
          :project="project"
          :sample="sample"
          :populateArea="populateAreaDebounced"
          :createUpdateSolarGroup="createUpdateSolarGroup"
          :removeSolarGroupPanels="removeSolarGroupPanels"
          :addOffsetToPanels="addOffsetToPanels"
          :removeSolarGroup="removeSolarGroup"
          :recenterCameraOnArea="recenterCameraOnArea"
          :anonymousUser="anonymousUser"
          :likedPanel="likedPanel"
          :likedPanelId="likedPanelId"
          :starClicked="starClicked"
          :disableRestrictedAreaMode="disableRestrictedAreaMode"
          :disableIndividualPlacementMode="disableIndividualPlacementMode"
          :unlikePanel="unlikePanel"
          :loadCustomPanelTextures="loadCustomPanelTextures"
          :getUserDefaultSolarValues="getUserDefaultSolarValues"
          :active="active"
          :individualPanelOrientation="individualPanelOrientation"
          :enableIndividualPlacementMode="enableIndividualPlacementMode"
          :validateIndividualPanels="validateIndividualPanels"
          :changePanelTypeForIndividualPanels="
            changePanelTypeForIndividualPanels
          "
          :solarGroupIndex="selectedSolarGroupIndex"
          @setupCompleted="unselectSolarArea"
          @restrictedAreasPlacement="() => toggleActive(6)"
          @solarGroupDeleted="unselectSolarArea"
          @defaultValuesUpdates="resyncDefaultValuesOnUpdate"
          @toggle-hotkeys="toggleHotkeys"
        />
        <GalleryWrapper
          :active="active"
          :projectId="project.id"
          :technicalCategoryId="project.technicalCategoryId"
          :bestImage="bestImage"
          :bestImageBasename="bestImageBasename"
          :cameraSource="cameraSource"
          :toggleActive="toggleActive"
          :initializeGalleryMode="initializeGalleryMode"
          :toggleFullScreenImageActive="toggleFullScreenImageActive"
          :fullScreenImageActive="fullScreenImageActive"
          :bestImagesPoints="bestImagesPoints"
          :mousePositionOnScreen="mousePositionOnScreen"
          :calculateOffsetForFullScreen="calculateOffsetForFullScreen"
          :originalImageWidth="originalImageWidth"
          :originalImageHeight="originalImageHeight"
          :changeCursorToLoading="changeCursorToLoading"
          :thumbnailsAvailable="thumbnailsAvailable"
          :setThumbnailsAvailable="setThumbnailsAvailable"
          :isEmployee="currentUser.isEmployee"
          :currentUserId="currentUser.id"
          :galleryWarningShown="galleryWarningShown"
          :usingBlurredImages="usingBlurredImages"
          :fetchCamerasWithImages="fetchCamerasWithImages"
          :addAnnotationMarkerToModel="addAnnotationMarkerToModel"
          :removeAnnotationMarkerFromModel="removeAnnotationMarkerFromModel"
          :annotationOpenRequest="annotationOpenRequest"
          :allAnnotations="allAnnotations"
          @update:allAnnotations="updateAllAnnotations"
          @close="() => toggleActive(0)"
          @openCsvVisualizer="openCsvVisualizer"
          :camerasDisplayed="camerasDisplayed"
          @toggleCameras="
            camerasDisplayed ? changeCamerasVisibility(false) : displayCameras()
          "
        />
        <!-- A damage opened from outside the gallery: its own photo on its
             own, with the annotation drawn on it and the panel beside it. -->
        <FullScreenImage
          v-if="damageImage"
          standalone
          :image="damageImage"
          :is-loading="damageImageLoading"
          :cameraSource="cameraSource"
          :calculateOffsetForFullScreen="calculateOffsetForFullScreen"
          :originalImageWidth="originalImageWidth || 0"
          :originalImageHeight="originalImageHeight || 0"
          :initialAnnotations="allAnnotations"
          :addAnnotationMarkerToModel="addAnnotationMarkerToModel"
          :removeAnnotationMarkerFromModel="removeAnnotationMarkerFromModel"
          :highlightAnnotationId="damageHighlightId"
          :isEmployee="currentUser.isEmployee"
          :currentUserId="currentUser.id"
          @close="closeDamageImage"
          @annotationSaved="refreshDamageAnnotations"
          @annotationDeleted="handleDamageDeleted"
        />
        <CsvVisualizer
          v-if="csvVisualizerVisible"
          :camerasCsv="camerasCsv"
          :imagesCountRc="csvVisualizerStats.imagesCountRc"
          :imagesCountNonRc="csvVisualizerStats.imagesCountNonRc"
          :useRcFlag="csvVisualizerStats.useRcFlag"
          :technicalCategoryId="csvVisualizerStats.technicalCategoryId"
          :projectId="project.id"
          @close="csvVisualizerVisible = false"
        />
      </div>
      <div v-if="company.logo && anonymousUser" class="position2">
        <span>
          <img style="padding-top: 10px" :src="company.logo" width="100" />
        </span>
      </div>
      <div
        v-if="hasPermission(Permission.View_Tools)"
        v-show="!fullScreenImageActive && !csvVisualizerVisible"
        id="panels-toolbar"
        class="position5 hide-mobile"
        :class="showDownloadAllDialog ? 'position5--disabled' : ''"
      >
        <div class="panels__toolbar main-menu">
          <div
            class="text-center panels__toolbar-text cursor-pointer"
            @click="(e) => toggleActive(0)"
          >
            <!-- AI and damage mode both run on top of Select so the model stays
                 clickable underneath, so neither may leave this lit as well —
                 two highlighted tools read as two active modes. -->
            <CursorIcon
              class="menu-icon"
              :color="
                active === 0 && !aiPanelOpen && !damageModeActive
                  ? activeColor
                  : 'white'
              "
            />
            <q-tooltip anchor="center right" self="center left">{{
              $t("toolbar.select")
            }}</q-tooltip>
          </div>
          <img
            v-if="hasPermission(Permission.View_Measure)"
            src="@/assets/icons/menu-item-line.svg"
          />
          <div
            v-if="hasPermission(Permission.View_Measure)"
            ref="measurementButton"
            class="text-center panels__toolbar-text cursor-pointer"
            @click="toggleActive(1)"
          >
            <MeasureIcon
              class="menu-icon"
              :color="measurementModeActive ? activeColor : 'white'"
            />
            <!-- Suppressed while measuring: the measurement menu opens next to
                 this button, so the hint would sit on top of it — and by then
                 the tool is self-evidently active. Uses v-if because QTooltip
                 has no `disable` prop; passing one is silently ignored. -->
            <q-tooltip
              v-if="!measurementModeActive"
              anchor="center right"
              self="center left"
              >{{ $t("toolbar.measure") }}</q-tooltip
            >
          </div>
          <img
            v-if="showSolarTools"
            src="@/assets/icons/menu-item-line.svg"
          />
          <div
            v-if="showSolarTools"
            class="text-center panels__toolbar-text cursor-pointer"
            @click="(e) => toggleActive(2)"
          >
            <SolarIcon
              class="menu-icon"
              :color="
                active === 2 || active === 3 || active === 6
                  ? activeColor
                  : 'white'
              "
            />
            <q-tooltip anchor="center right" self="center left">{{
              $t("toolbar.solar")
            }}</q-tooltip>
          </div>
          <img
            v-if="hasPermission(Permission.View_Gallery)"
            src="@/assets/icons/menu-item-line.svg"
          />
          <div
            v-if="hasPermission(Permission.View_Gallery)"
            class="text-center panels__toolbar-text cursor-pointer"
            @click="handleGalleryClick"
          >
            <GalleryIcon
              class="menu-icon"
              :color="active === 5 ? activeColor : 'white'"
            />
            <q-tooltip anchor="center right" self="center left">{{
              $t("toolbar.gallery")
            }}</q-tooltip>
          </div>
          <img
            v-if="activeUserIsEmployee"
            src="@/assets/icons/menu-item-line.svg"
          />
          <div
            v-if="activeUserIsEmployee"
            class="text-center panels__toolbar-text"
          >
            <CamerasIcon
              class="menu-icon cursor-pointer"
              :color="camerasDisplayed ? activeColor : 'white'"
              @click="
                camerasDisplayed
                  ? changeCamerasVisibility(false)
                  : displayCameras()
              "
            />
            <!-- A toggle, so the hint names what the click will do. -->
            <q-tooltip anchor="center right" self="center left">{{
              camerasDisplayed
                ? $t("toolbar.hideCameras")
                : $t("toolbar.showCameras")
            }}</q-tooltip>
          </div>
          <img
            v-if="showDamageAnnotations"
            src="@/assets/icons/menu-item-line.svg"
          />
          <div
            v-if="showDamageAnnotations"
            class="text-center panels__toolbar-text cursor-pointer"
            @click="handleDamageModeClick"
          >
            <DamageAnnotationsIcon
              class="menu-icon"
              :color="damageModeActive ? activeColor : 'white'"
            />
            <q-tooltip anchor="center right" self="center left">{{
              $t("toolbar.damageAnnotations")
            }}</q-tooltip>
          </div>
          <img
            v-if="showAiDesigner"
            src="@/assets/icons/menu-item-line.svg"
          />
          <div
            v-if="showAiDesigner"
            class="text-center panels__toolbar-text cursor-pointer"
            @click="toggleAiPanel"
          >
            <DesignerIcon
              class="menu-icon"
              :color="aiPanelOpen ? activeColor : 'white'"
            />
            <!-- Was a native `title`; switched to q-tooltip so every tool in
                 this palette shows its hint the same way. -->
            <q-tooltip anchor="center right" self="center left">{{
              $t("aiDesign.title")
            }}</q-tooltip>
          </div>
        </div>
      </div>
      <div
        v-if="measurementModeActive"
        class="measurement-menu hide-mobile"
        :style="measurementMenuPosition"
        @mouseover="measurementMenuExpanded = true"
        @mouseleave="showSmallMeasurementMenu"
      >
        <div class="panels__toolbar panels__toolbar--small main-menu--small">
          <div
            class="panels__toolbar-text panels__toolbar-text--small text-left q-mb-xs cursor-pointer"
            @click="(e) => toggleActive(1)"
          >
            <DistanceIcon
              class="menu-icon--small"
              :color="active === 1 ? activeColor : 'white'"
            />
            <span
              v-if="measurementMenuExpanded"
              class="q-ml-sm"
              :class="active === 1 ? 'menu-text--active' : ''"
              >{{ $t("modes.distance") }}</span
            >
          </div>
          <div
            class="panels__toolbar-text panels__toolbar-text--small text-left cursor-pointer"
            @click="(e) => toggleActive(4)"
          >
            <AreaIcon
              class="menu-icon--small"
              :color="active === 4 ? activeColor : 'white'"
            />
            <span
              v-if="measurementMenuExpanded"
              class="q-ml-sm"
              :class="active === 4 ? 'menu-text--active' : ''"
              >{{ $t("modes.area") }}</span
            >
          </div>
        </div>
      </div>
      <div
        class="position9 q-pa-sm"
        :class="isFrontendOnlyDemo ? 'position9--sample' : ''"
      >
        <span
          class="q-mx-xs cursor-pointer hide-mobile hide-iframe nav-button"
          :class="{ 'nav-button--active': navigationMenu === 0 }"
          style="padding-right: 2px; padding-top: 4px; padding-bottom: 4px"
          @click="handleOrbitClick"
        >
          <OrbitIcon
            style="width: 30px; height: 30px; object-fit: contain"
            color="white"
          />
          <q-tooltip>{{ $t("navigation.orbitTooltip") }}</q-tooltip>
        </span>
        <img
          class="hide-mobile hide-iframe"
          style="margin-left: 10px; margin-right: 6px"
          src="@/assets/icons/navigation-menu-break.svg"
        />
        <span
          class="q-ml-sm q-mr-md cursor-pointer hide-mobile hide-iframe nav-button"
          :class="{ 'nav-button--active': navigationMenu === 1 }"
          @click="handlePanClick"
        >
          <PanIcon style="width: 26px" color="white" />
          <q-tooltip>{{ $t("navigation.panTooltip") }}</q-tooltip>
        </span>
        <img
          class="hide-mobile hide-iframe"
          src="@/assets/icons/navigation-menu-break.svg"
        />
        <span
          v-if="featureFlag('viewer_camera_mode')"
          class="q-mx-sm cursor-pointer hide-mobile hide-iframe"
          @click="toggleCameraMode"
        >
          <img
            style="width: 45px"
            v-show="!isOrthographicCamera"
            src="@/assets/icons/camera-orthographic.svg"
          />
          <img
            style="width: 65px"
            v-show="isOrthographicCamera"
            src="@/assets/icons/camera-perspective.svg"
          />
          <!-- The icon shows the mode you would switch to, so the hint names
               that mode rather than the current one. -->
          <q-tooltip>{{
            isOrthographicCamera
              ? $t("navigation.perspectiveTooltip")
              : $t("navigation.orthographicTooltip")
          }}</q-tooltip>
        </span>
        <span
          v-if="isBobbieContext"
          class="q-mx-sm cursor-pointer"
          @click="resetCameraPosition"
        >
          <FullScreenNavIcon
            style="width: 30px"
            :color="showGreenFullScreen ? activeColor : 'white'"
          />
          <q-tooltip>{{ $t("navigation.resetViewTooltip") }}</q-tooltip>
        </span>
        <span
          v-if="featureFlag('viewer_view_planes')"
          class="view-plane-button cursor-pointer q-mx-xs"
          @click="
            viewPlane = 'XY';
            resetCameraPosition();
          "
        >
          XY
          <q-tooltip>{{ $t("navigation.topViewTooltip") }}</q-tooltip>
        </span>
        <span
          v-if="featureFlag('viewer_view_planes')"
          class="view-plane-button cursor-pointer q-mx-xs"
          @click="
            viewPlane = 'YZ';
            resetCameraPosition();
          "
        >
          YZ
          <q-tooltip>{{ $t("navigation.sideViewTooltip") }}</q-tooltip>
        </span>
        <span
          v-if="featureFlag('viewer_view_planes')"
          class="view-plane-button cursor-pointer q-mx-xs"
          @click="
            viewPlane = 'XZ';
            resetCameraPosition();
          "
        >
          XZ
          <q-tooltip>{{ $t("navigation.frontViewTooltip") }}</q-tooltip>
        </span>
        <img
          class="q-mx-md hide-mobile hide-iframe"
          src="@/assets/icons/navigation-menu-break.svg"
        />
        <span
          class="q-mx-sm cursor-pointer hide-mobile hide-iframe nav-button"
          :class="{ 'nav-button--active': openHelperModal }"
          @click="openHelpModal"
        >
          <img
            class="help-button"
            src="@/assets/icons/help-glyph.svg"
            alt="help"
          />
          <q-tooltip>{{ $t("navigation.guideTooltip") }}</q-tooltip>
        </span>
      </div>
      <div
        id="arrow-container"
        class="position11"
        :class="{
          'position11--shifted':
            selectedArea && selectedArea.type === 'SOLAR_GROUP',
        }"
      ></div>
    </div>
    <AirteamViewerHelper
      :isOpen="openHelperModal"
      @helper-closed="closeHelperModal"
    />
    <DetectDamageModal
      v-model="damageModalOpen"
      :loading="damageOptInLoading"
      :no-damages-found="damageNoDamagesFound"
      :reviewed="damageReviewed"
      @confirm="confirmDamageDetection"
      @cancel="damageModalOpen = false"
    />
    <!-- Teleported so no ancestor's stacking context can trap the dialog
         below other chrome; the viewer nests it deep in the canvas overlay. -->
    <Teleport to="body">
      <div
        v-if="shareModalOpen"
        class="share-model-backdrop"
        @click="shareModalOpen = false"
      ></div>
      <ShareModelModal
        v-if="shareModalOpen"
        :projectId="project.id"
        :language="$i18n.locale"
        :isBobbie="isBobbieContext"
        @close="shareModalOpen = false"
        @shared="onModelShared"
      />
    </Teleport>
  </div>
  <el-dialog
    v-model="dialogVisible"
    width="580"
    style="background-color: var(--color-dark-bg-primary); border-radius: 10px"
  >
    <template #header>
      <img
        v-if="!isBobbieContext"
        src="@/assets/icons/check-o.svg"
        alt="icon"
        class="check-icon"
        style="width: 20px; height: 20px; vertical-align: middle"
      />
      <img
        v-else
        src="@/assets/icons/check-o-blue.svg"
        alt="icon"
        class="check-icon"
        style="width: 20px; height: 20px; vertical-align: middle"
      />
      <span class="share-title">{{ $t("share.title") }}</span>
    </template>
    <div class="share-message-body">
      <div>{{ $t("share.message1") }}</div>
      <div>{{ $t("share.message2") }}</div>
      <div class="valid">{{ $t("share.valid") }}</div>
    </div>
    <template #footer>
      <div>
        <el-button
          class="share-close-button"
          type="text"
          @click="dialogVisible = false"
        >
          {{ $t("share.close") }}
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import * as THREE from "three";
import { mapActions, mapGetters } from "vuex";
import { TransformControls } from "@/modules/TransformControls";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { ElMessage } from "element-plus";
import { ref, toRaw } from "vue";
import { CSS2DRenderer } from "three/addons/renderers/CSS2DRenderer.js";
import { MeshBVH, acceleratedRaycast } from "three-mesh-bvh";
import AirteamViewerHelper from "@/components/AirteamViewerHelper.vue";
import FirstVisitHint from "@/components/FirstVisitHint.vue";
import ShareModelModal from "@/components/ShareModelModal.vue";
import { isProduction } from "@/utils/env.js";
import API from "@/api/API";
import annotationsApi from "@/api/endpoints/annotations.js";
import { decodeJwt } from "@/utils/decode-jwt.js";
import {
  hasPermission,
  Permission,
  ShareAccessLevel,
  setAnonymousRole,
  setFrontendOnlyDemoRole,
} from "@/constants/permissions";
import { CLIENT_VIEWER } from "@/constants/roles";
import { trackEvent } from "@/utils/analytics.js";
import CameraControls from "camera-controls";
import { H } from "highlight.run";
import * as areaMethods from "@/modules/area";
import * as restrictedAreaMethods from "@/modules/restrictedArea";
import * as undoRedoMethods from "@/modules/undoRedo";
import * as measurementMethods from "@/modules/measurement";
import * as areaMeasurementMethods from "@/modules/areaMeasurement";
import * as panelMethods from "@/modules/panel";
import * as fixedTiltMethods from "@/modules/fixedTilt";
import * as eastWestMethods from "@/modules/eastWest";
import * as commonMethods from "@/modules/common";
import * as authZitadelMethods from "@/services/authService.js";
import * as navigationMethods from "@/modules/navigation";
import * as cameraMethods from "@/modules/camera";
import * as galleryMethods from "@/modules/gallery";
import { initializeModes } from "@/modules/modes.js";
import { changeLocale } from "@/plugins/i18n";
import {
  storeLanguagePreference,
  getStoredLanguagePreference,
} from "@/utils/language-persistence";
import {
  hasSeenNavigationHintLocally,
  markNavigationHintSeenLocally,
} from "@/utils/navigation-hint-persistence";
import { getGPUTier } from "detect-gpu";
import {
  WHITE,
  VERTICAL,
  HORIZONTAL,
  TEST_COMPANIES,
  TECHNICAL_PICTURES_CATEGORY,
  BLURRED_TECHNICAL_PICTURES_CATEGORY,
} from "@/constants";
// removed due to mk3 split release
import MainSidebar from "@/components/NewSolarSidebar/MainSidebar.vue";
import AiDesignComponent from "@/components/NewSolarSidebar/AiDesignComponent.vue";
import SolarSidebar from "@/components/RightSidebar/SolarSidebar.vue";
import GalleryWrapper from "@/components/Gallery/GalleryWrapper.vue";
import FullScreenImage from "@/components/Gallery/FullScreenImage.vue";
import CsvVisualizer from "@/components/Gallery/CsvVisualizer.vue";
import BreadCrumbs from "@/components/NewSolarSidebar/BreadCrumbs.vue";
import ToggleSwitch from "@/components/common/ToggleSwitch.vue";
import newMarker from "@/assets/model/new_marker_3.svg";
import Stats from "stats.js";
import moment from "moment";
import {
  fetchXlsxBlob,
  fetchModelBlob,
  sizedImgTag,
  renderHtmlToPdfBlob,
} from "@/components/Export/exportSources.js";
import { buildObjFiles } from "@/components/Export/objExport.js";
import {
  buildExportRoot,
  exportGlbBlob,
  collectMaterialUuids,
  hasExportablePanels,
} from "@/components/Export/glbExport.js";
import ExportModal from "@/components/Export/ExportModal.vue";
import { getCookie } from "../utils/cookie-utils";
import { BLURRED_CATEGORY_INDEX } from "../constants";
import { bobbieTheme } from "@/scss/_theme-bobbie.js";
import { theme as defaultTheme } from "@/scss/_theme.js";
import { initViewerColors } from "@/utils/viewer-colors";
import {
  defaultDistanceName,
  defaultAreaName,
  defaultPvArrayName,
} from "@/utils/annotation-names";
import CursorIcon from "@/components/icons/CursorIcon.vue";
import MeasureIcon from "@/components/icons/MeasureIcon.vue";
import SolarIcon from "@/components/icons/SolarIcon.vue";
import GalleryIcon from "@/components/icons/GalleryIcon.vue";
import DistanceIcon from "@/components/icons/DistanceIcon.vue";
import AreaIcon from "@/components/icons/AreaIcon.vue";
import FullScreenNavIcon from "@/components/icons/FullScreenNavIcon.vue";
import LocationIcon from "@/components/icons/LocationIcon.vue";
import PanIcon from "@/components/icons/PanIcon.vue";
import OrbitIcon from "@/components/icons/OrbitIcon.vue";
import UploadIcon from "@/components/icons/UploadIcon.vue";
import DamageAnnotationsIcon from "@/components/icons/DamageAnnotationsIcon.vue";
import DesignerIcon from "@/components/icons/DesignerIcon.vue";
import CamerasIcon from "@/components/icons/CamerasIcon.vue";
import ScreenshotActionIcon from "@/components/icons/ScreenshotActionIcon.vue";
import ShareActionIcon from "@/components/icons/ShareActionIcon.vue";
import ThemeSwitchIcon from "@/components/icons/ThemeSwitchIcon.vue";
import DetectDamageModal from "@/components/DetectDamageModal.vue";
import {
  damageDetectionAvailable,
  damageDetectionActivated,
} from "@/utils/damage-detection.js";
CameraControls.install({ THREE: THREE });

// The letter is baked into the model, so it has to be drawn inside the canvas —
// but the design puts it outside the compass disc, standing clear of it. The
// canvas is therefore wider than the disc (which is painted by
// `.position11::before` at $compass-disc), and the gap between needle and letter
// lands in that surrounding margin. At these values the needle fills about half
// the disc, the horizon ring traces its rim, and the letter stands ~13px tall
// about 4px past the edge. Keep $compass-canvas in the stylesheet in step.
const COMPASS_CANVAS_PX = 114;
const COMPASS_CAMERA_DISTANCE = 14;
// Material names baked into northarrow_v3.glb by scripts/build-northarrow.py.
// The letter, the horizon ring and the needle's south half all get retinted for
// the viewer theme; the red north half is left alone.
const COMPASS_TINTED_MATERIALS = [
  "NorthLetter",
  "HorizonRing",
  "CompassSouth",
];
// On the dark theme these read as light-on-dark, except the needle's south half,
// which keeps whatever grey the model was authored with. On the light theme all
// three take the slate ink the viewer uses for light-theme canvas chrome — a
// pale south half was almost invisible against it.
const COMPASS_INK_DARK = 0xe6e6e6;
const COMPASS_INK_LIGHT = 0x26415a;

export default {
  name: "AirteamViewer",
  components: {
    ExportModal,
    AirteamViewerHelper,
    FirstVisitHint,
    ShareModelModal,
    ToggleSwitch,
    MainSidebar,
    AiDesignComponent,
    DamageAnnotationsIcon,
    DesignerIcon,
    CamerasIcon,
    ScreenshotActionIcon,
    ShareActionIcon,
    ThemeSwitchIcon,
    DetectDamageModal,
    SolarSidebar,
    BreadCrumbs,
    CsvVisualizer,
    GalleryWrapper,
    FullScreenImage,
    CursorIcon,
    MeasureIcon,
    SolarIcon,
    GalleryIcon,
    DistanceIcon,
    AreaIcon,
    FullScreenNavIcon,
    LocationIcon,
    PanIcon,
    OrbitIcon,
    UploadIcon,
  },
  props: ["isFrontendOnlyDemo", "thermal"],
  data() {
    return {
      Permission,
      // Keep for compatibility with helper functions
      projectId: null,
      companyId: null,
      // -----------

      project: {
        id: null,
        modelId: null,
        modelPath: null,
        flightDate: null,
        address: null,
        orientation: {
          x: -90,
          y: 0,
          z: 0,
        },
        scale: 1.0,
        technicalCategoryId: null,
        solarModeAccessible: true,
      },
      savedOrientation: {
        x: -90,
        y: 0,
        z: 0,
      },
      company: {
        id: null,
        logo: null,
      },
      currentUser: {
        id: null,
        isEmployee: null,
        isBobbieUser: false,
      },

      pinpointMaterial: null,
      mousePositionOnScreen: {
        x: 0,
        y: 0,
      },
      bestImagesPoints: [],
      fromDraggStart: false,
      temporaryMeasurement: null,
      selectedMeasurement: null,
      bestImage: null,
      bestImageBasename: null,
      detectedMeasurementArea: null,
      isMeasurementSelected: false,
      previousSolarArea: null,
      stencilCount: 1,
      measurementDrag: false,
      objectIsloaded: null,
      measurementAreaCreated: null,
      likedPanel: null,
      likedPanelId: null,
      cameraObject: null,
      cameraControls: null,
      perspectiveCamera: null,
      orthographicCamera: null,
      transformControls: null,
      infiniteGridHelper: null,
      clock: null,
      zoomCenterDot: null,
      lightning: false,
      container: null,
      controls: null,
      renderer: null,
      labelRenderer: null,
      lighting: null,
      ambient: null,
      keyLight: null,
      fillLight: null,
      backLight: null,
      active: 0,
      mouse: null,
      spriteMaterial: null,
      dragOn: false,
      areas: [],
      measurementAreas: [],
      sidebarOpen: window.innerWidth > 600,
      aiPanelOpen: false,
      damageModeActive: false,
      damageImage: null,
      damageImageLoading: false,
      damageHighlightId: null,
      damageModalOpen: false,
      damageOptInLoading: false,
      damageNoDamagesFound: false,
      damageNoDamagesNoticeShownFor: null,
      damageReviewed: false,
      damageReviewedNoticeShownFor: null,
      aiPreviewImage: null,
      areaExpanded: true,
      cameraMesh: null,
      panelAdditionType: null,
      panelType: "",
      panelSpacing: null,
      selectedArea: null,
      panelStep: 0,
      panelSetupConfirmed: false,
      pmremGenerator: null,
      existingAreasIds: [],
      existingMeasurementsIds: [],
      neutralEnvironment: null,
      callCount: 0,
      movingLeft: true,
      isEditButtonDisplayed: false,
      pointSelected: null,
      screenshot: null,
      isCtrlDown: false,
      isAltDown: false,
      isShiftDown: false,
      combinedMatrix: null,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      selectionBox: null,
      positions: {
        clientX: undefined,
        clientY: undefined,
        movementX: 0,
        movementY: 0,
      },
      measurementLine: null,
      currentMeasurementAreaLines: [],
      areaEndingLine: null,
      selectedMeasurementArea: null,
      panelAlignment: false,
      panelCounter: 1,
      openHelperModal: false,
      // The first-visit overlay over the viewer; the first press or scroll
      // anywhere dismisses it and records it as seen.
      showFirstVisitHint: false,
      texturePath: "/assets/textures/",
      texturesLoading: true,
      trashSize: {
        width: 0.5,
        height: 0.5,
      },
      verticalPanelTypes: [],
      horizontalPanelTypes: [],
      customSizePanel: {
        width: null,
        height: null,
      },
      undoStack: [],
      redoStack: [],
      currentMeasurementLines: [],
      orientationCardExpanded: false,
      summaryExpanded: false,
      scaleCardExpanded: false,
      isOrthographicCamera: false,
      rotationSpeed: 0.001,
      upperAngleRange: 0.7854, // 45 degrees in radians
      lowerAngleRange: -0.7854,
      anonymousUser: false,
      sessionStartTime: null,
      measurements: [],
      mouseOverModel: false,
      canvasElementRef: null,
      navigationMenu: null,
      showGreenFullScreen: false,
      panelsToUpdate: [],
      measurementModeActive: false,
      measurementMenuExpanded: false,
      dotGeometry: null,
      middotGeometry: null,
      timestamps: [],
      currentPixelRatio: null,
      loading: false,
      loadingProgress: 0,
      annotationRenderOrder: 0,
      undoLoading: false,
      redoLoading: false,
      solarDefaultValues: null,
      exportedManually: false,
      stats: null,
      showFps: false,
      fpsResizeObserver: null,
      individualPanelOrientation: null,
      lastUpdatedTime: 0,
      throttleInterval: 15,
      isLightMode: false,
      gradientBackgroundTexture: null,
      dialogVisible: ref(false),
      shareModalOpen: false,
      camerasCsv: [],
      cameraTransforms: [],
      cameraSource: null,
      camerasFrustums: [],
      camerasFrustumsByFileId: null,
      photoIdByGalleryPk: null,
      galleryPoint: null,
      annotationMarkers: [],
      annotationMarkersHidden: true,
      annotationOpenRequest: null,
      allAnnotations: [],
      selectedAnnotationId: null,
      sampleModelName: null,
      fullScreenImageActive: false,
      originalImageWidth: null,
      originalImageHeight: null,
      cameraPositionsCsvUrl: null,
      cameraWorkers: [],
      workerInitialized: null,
      workerResults: null,
      completedWorkers: 0,
      totalWorkers: 0,
      isProcessingLineOfSight: false,
      csvAvailable: true,
      camerasLoadedWithImages: false,
      galleryWarningShown: false,
      thumbnailsAvailable: null,
      galleryPointsSelected: 0,
      populateAreaTimeout: null,
      populateAreaPending: false,
      populateAreaDelay: 200,
      csvVisualizerVisible: false,
      csvVisualizerStats: {
        imagesCountRc: 0,
        imagesCountNonRc: 0,
        useRcFlag: true,
        technicalCategoryId: null,
      },
      viewPlane: "XY",
      viewPlaneDropdownOpen: false,
      usingBlurredImages: false,
      loadedModelUrl: null,
      exportModalOpen: false,
      // Snapshot taken in openExportModal(). Panel meshes live on non-reactive
      // Three.js objects, so this cannot be a computed — placing an array
      // would never invalidate it.
      exportPanelsAvailable: false,
    };
  },
  watch: {
    panelSpacing() {
      if (this.panelSpacing?.includes(",")) {
        this.panelSpacing = this.panelSpacing.replace(/,/g, ".");
      }
    },
    orientationCardExpanded(isExpanded) {
      if (isExpanded) {
        this.enableTransformControls();
      } else {
        this.disableTransformControls();
        if (this.isOrthographicCamera) {
          this.toggleCameraMode();
        }
      }
    },
    aiPanelOpen() {
      this.$nextTick(() => this.onWindowResize());
    },
    aiPreviewImage(val) {
      if (this.cameraControls) {
        this.cameraControls.enabled = !val;
      }
    },
    showFps(value) {
      if (this.stats) {
        this.stats.dom.style.display = value ? "block" : "none";
        if (value) this.updateFpsPosition();
      }
    },
  },
  computed: {
    ...mapGetters(["customPanels", "activeUserIsEmployee", "addonEnabled"]),
    showSolarTools() {
      return (
        this.featureFlag("solar-planning-features") &&
        hasPermission(Permission.View_Solar) &&
        (this.activeUserIsEmployee || this.project.solarModeAccessible) &&
        this.addonEnabled("solarModeEnabled")
      );
    },
    // Width of the canvas column. A model-only share has no sidebar and no
    // collapsed-sidebar strip beside it either, so it spans the full width the
    // same way the frontend-only demo does.
    viewerColumnClass() {
      if (this.isFrontendOnlyDemo || !hasPermission(Permission.View_Tools)) {
        return "col-100";
      }
      if (!this.sidebarOpen) return "col-115";
      return this.addonEnabled("airteamDesignerEnabled") && this.aiPanelOpen
        ? "col-lg-8 col-md-7 col-sm-6 hide-mobile"
        : "col-lg-10 col-md-9 col-sm-8 hide-mobile";
    },
    // True while an overlay owns the keyboard. The viewer's key handlers live on
    // `document`, so without this they keep firing underneath a dialog: typing
    // an address into the share dialog swapped the view plane, and Backspace
    // deleted points from whatever was being drawn behind it.
    //
    // Deliberately does not cover the shift/alt bookkeeping in keyDownInit /
    // keyUpInit — that is navigation state rather than a shortcut, and skipping
    // half of it would leave isShiftDown stuck on.
    keyboardShortcutsSuspended() {
      return this.shareModalOpen;
    },
    showAiDesigner() {
      return (
        !this.anonymousUser && this.addonEnabled("airteamDesignerEnabled")
      );
    },
    // Gated on the inspections beta while the detection is in beta. The damages
    // themselves are saved project data, so the tool otherwise follows the same
    // permission as the markers it reveals rather than an edit right — a viewer
    // may opt in and look, they just cannot persist the opt-in.
    showDamageAnnotations() {
      return (
        !this.anonymousUser &&
        hasPermission(Permission.View_DamageAnnotations) &&
        damageDetectionAvailable()
      );
    },
    aiDamagesOptedIn() {
      return damageDetectionActivated();
    },
    bannerKey() {
      if (this.project?.isSampleProject) return "sample";
      const roles = this.$store.getters.roles || [];
      if (roles.includes(CLIENT_VIEWER) && !this.isFrontendOnlyDemo)
        return "viewer";
      return null;
    },
    isBobbieContext() {
      return this.$store.state.auth?.isBobbieContext || false;
    },
    isInIFrameContext() {
      return this.$store.state.auth?.isInIFrameContext || false;
    },
    activeColor() {
      return this.isBobbieContext ? "#2EAFFF" : "#23E7A5";
    },
    featureFlag() {
      // Returns a function so templates can call featureFlag('flag_name')
      return (name) => this.$store.getters.featureFlag(name);
    },
    sections() {
      return this.$store.state.section;
    },
    solarSectionVisible() {
      return this.sections.config.solar_groups_visible;
    },
    modes() {
      return initializeModes(this);
    },
    restrictedAreaMode() {
      const mode = this.modes.find((mode) => mode.name === "RESTRICTED_AREA");
      return mode.enabled;
    },
    getViewerWidth() {
      if (this.isFrontendOnlyDemo) return 1.0;
      if (this.sidebarOpen)
        return this.addonEnabled("airteamDesignerEnabled") && this.aiPanelOpen
          ? 0.66667
          : 0.83333;
      return 0.98;
    },
    completedAreas() {
      return this.areas.filter((area) => area.plane);
    },
    chosenPanel() {
      return this.panelTypes.find((item) => item.name === this.panelType);
    },
    totalNumberOfPanels() {
      return this.areas
        .filter(
          (area) => (area.plane || area.instancedMesh) && area.show !== false
        )
        .map((area) => this.getAreaPanelsCount(area))
        .reduce((a, b) => a + b, 0);
    },
    totalRoofArea() {
      let totalArea = 0;
      const visibleAreas = this.areas.filter((area) => area.show !== false);
      for (let area of visibleAreas) {
        totalArea += Number(this.calculateSurfaceArea(area));
      }
      return totalArea.toFixed(2);
    },
    totalPanelsArea() {
      let totalArea = 0;
      const visibleAreas = this.areas.filter(
        (area) => area.plane && area.show !== false
      );

      for (let area of visibleAreas) {
        totalArea += this.calculateVisiblePanelsArea(area);
      }
      return totalArea.toFixed(2);
    },
    panelTypes() {
      const panelsList = this.panelAlignment
        ? this.horizontalPanelTypes
        : this.verticalPanelTypes;

      const filteredCustomPanels = this.customPanels.filter((panel) => {
        if (
          panel.show &&
          (panel.companyId || panel.userId === this.currentUser.id)
        ) {
          return this.panelAlignment
            ? panel.orientation === HORIZONTAL
            : panel.orientation === VERTICAL;
        }
      });
      return [...filteredCustomPanels, ...panelsList];
    },
    isFirstPoint() {
      if (this.areas.length === 0 || this.areas[this.areas.length - 1].closed)
        return true;
      return false;
    },
    isFirstMeasurementPoint() {
      if (
        this.measurementAreas.length === 0 ||
        this.measurementAreas[this.measurementAreas.length - 1].closed
      )
        return true;
      return false;
    },
    modelRotation() {
      return this.modelObject.children[0].rotation;
    },
    currentLeftClickAction() {
      if (this.active === 0) return CameraControls.ACTION.ROTATE;
      return CameraControls.ACTION.NONE;
    },
    camerasDisplayed() {
      return this.cameraMesh?.visible;
    },
    selectedSolarGroupIndex() {
      const closedAreas = this.areas.filter((area) => area.closed);
      return (
        closedAreas.findIndex((area) => area.id === this.selectedArea?.id) + 1
      );
    },
    measurementMenuPosition() {
      if (!this.$refs.measurementButton) {
        return { top: "40vh", left: "60px" }; // fallback to original values
      }

      const buttonRect =
        this.$refs.measurementButton.$el || this.$refs.measurementButton;
      if (!buttonRect || !buttonRect.getBoundingClientRect) {
        return { top: "40vh", left: "60px" }; // fallback to original values
      }

      const rect = buttonRect.getBoundingClientRect();
      const top = rect.top + rect.height / 2;

      // Attach to the toolbar's right edge instead of a fixed offset, so the
      // menu stays flush with it whatever the toolbar's width. The menu is a
      // sibling of the toolbar, so both are positioned inside the toolbar's
      // offsetParent; convert the button's viewport edge into that frame.
      const container = buttonRect
        .closest("#panels-toolbar")
        ?.offsetParent?.getBoundingClientRect();
      const left = rect.right - (container?.left ?? 0);

      return {
        top: `${top}px`,
        left: `${left}px`,
        transform: "translateY(-50%)",
      };
    },
    xlsxConfigState() {
      return this.$store.state.section.config;
    },
    showExportCta() {
      console.log(this.addonEnabled("exportCapabilitiesEnabled"))
      return (
        !this.anonymousUser &&
        !this.isFrontendOnlyDemo &&
        this.addonEnabled("exportCapabilitiesEnabled")
      );
    },
    // Bobbie keeps its own branding, so light mode is not offered there —
    // neither to signed-in Bobbie clients nor to their shared links.
    isBobbieCustomer() {
      return this.isBobbieContext || this.currentUser.isBobbieUser === true;
    },
    showThemeToggle() {
      return !this.isBobbieCustomer;
    },
    // Anonymous visitors and sample models have no user record to persist to,
    // so their theme choice stays session-only (dark by default).
    syncThemeWithBackend() {
      return (
        this.showThemeToggle && !this.anonymousUser && !this.isFrontendOnlyDemo
      );
    },
    exportItems() {
      const t = this.$t.bind(this);
      const items = [];

      // PV Array Report (PDF) — hidden entirely when the solar flag is off.
      if (this.featureFlag("solar-planning-features")) {
        const hasArrays = this.areas.some((a) => a.show !== false);
        items.push({
          id: "pv",
          category: t("exportPanel.catReports"),
          name: t("exportPanel.pvName"),
          description: t("exportPanel.pvDesc"),
          format: "PDF",
          state: hasArrays
            ? { type: "enabled" }
            : { type: "disabled", reason: t("exportPanel.pvNoArrays") },
          getFiles: async () => {
            const html = await this.buildReportHtml();
            const filename = this.exportFilename(t("exportPanel.pvName"), "pdf");
            const blob = await renderHtmlToPdfBlob(html, filename);
            if (!TEST_COMPANIES.includes(this.company.id)) {
              trackEvent("generate_ffsolar_report", {
                page_title: "AirteamViewer",
                page_location: window.location.href,
                page_path: window.location.pathname,
                project_id: this.project.id,
                isCustomer: !this.currentUser.isEmployee,
                customer_type: this.currentUser.isEmployee
                  ? "employee"
                  : "customer",
              });
            }
            return [{ name: filename, blob }];
          },
        });
      }

      // Damage Report (PDF) — offered under the same permission/add-on gate as
      // the damage tool itself, so a viewer who cannot see damages is not shown
      // a report of them.
      if (this.showDamageAnnotations) {
        const hasDamages = this.allAnnotations.length > 0;
        items.push({
          id: "damage",
          category: t("exportPanel.catReports"),
          name: t("exportPanel.damageName"),
          description: t("exportPanel.damageDesc"),
          format: "PDF",
          state: hasDamages
            ? { type: "enabled" }
            : { type: "disabled", reason: t("exportPanel.damageNoData") },
          getFiles: async () => {
            const html = await this.buildDamageReportHtml();
            const filename = this.exportFilename(
              t("exportPanel.damageName"),
              "pdf"
            );
            const blob = await renderHtmlToPdfBlob(html, filename);
            return [{ name: filename, blob }];
          },
        });
      }

      // Annotations Table (XLSX)
      // Mirrors the two filters buildXlsxPayload applies, so the row is offered
      // only when at least one annotation would actually reach the file.
      const hasAnnotationData =
        (this.xlsxConfigState.distances_visible &&
          this.measurements.some((distance) => distance.show)) ||
        (this.xlsxConfigState.measurements_visible &&
          this.measurementAreas.some((area) => area.show)) ||
        (this.xlsxConfigState.solar_groups_visible &&
          this.areas.some((area) => area.show));
      items.push({
        id: "xlsx",
        category: t("exportPanel.catDataTables"),
        name: t("exportPanel.xlsxName"),
        description: t("exportPanel.xlsxDesc"),
        format: "XLSX",
        state: hasAnnotationData
          ? { type: "enabled" }
          : { type: "disabled", reason: t("exportPanel.xlsxNoData") },
        getFiles: async () => {
          const filename = this.exportFilename(
            t("exportPanel.xlsxName"),
            "xlsx"
          );
          const { blob, companyName } = await fetchXlsxBlob(
            this.buildXlsxPayload()
          );
          trackEvent("xlsx_data_requested_ff", {
            event_label: "Excel Data Downloaded",
            date: moment().format("DD.MM.YY + HH.mm"),
            project_id: this.project.id,
            file_name: filename,
            company_name: companyName,
            environment: isProduction ? "production" : "staging",
            customer_type: this.currentUser.isEmployee
              ? "employee"
              : "customer",
          });
          return [{ name: filename, blob }];
        },
      });

      // 3D model — format chosen via the modal dropdown (GLB default).
      // Withheld entirely when the subscription has no 3D-model export add-on.
      if (this.addonEnabled("exportThreeDModelEnabled")) {
        items.push({
          id: "model",
          category: t("exportPanel.cat3dCad"),
          name: t("exportPanel.glbName"),
          description: t("exportPanel.glbDesc"),
          format: "3D", // icon label (see FileTypeIcon)
          formats: ["GLB", "OBJ"], // dropdown options
          state: this.objectIsloaded
            ? { type: "enabled" }
            : { type: "disabled", reason: t("exportPanel.glbLoading") },
          option: {
            key: "includeArrays",
            label: t("exportPanel.includeArrays"),
            available: this.exportPanelsAvailable,
            reason: t("exportPanel.includeArraysNone"),
          },
          getFiles: async ({
            format = "GLB",
            includeArrays = false,
            onProgress,
            signal,
          } = {}) => {
            const isObj = format === "OBJ";
            const withArrays = includeArrays && this.exportPanelsAvailable;
            const report = (fraction, label) => onProgress?.(fraction, label);
            const suffix = withArrays ? "_with_pv" : "";
            let files;
            let filename;

            // OBJ still needs the bucket bytes — its writer reuses the original
            // texture atlas from them instead of re-encoding it through a canvas.
            let sourceGlb = null;
            if (isObj) {
              const blob = await fetchModelBlob(this.loadedModelUrl, {
                signal,
                onProgress: (f) =>
                  report(f * 0.35, t("exportPanel.stageDownloading")),
              });
              sourceGlb = await blob.arrayBuffer();
            }

            report(
              isObj ? 0.35 : 0.02,
              t(withArrays ? "exportPanel.stageArrays" : "exportPanel.stageModel")
            );
            const root = await buildExportRoot({
              modelObject: toRaw(this.modelObject),
              areas: this.areas.map((area) => toRaw(area)),
              includeArrays: withArrays,
              signal,
              onProgress: (f) =>
                report(
                  (isObj ? 0.35 : 0.02) + f * 0.35,
                  t(
                    withArrays
                      ? "exportPanel.stageArrays"
                      : "exportPanel.stageModel"
                  )
                ),
            });
            try {
              report(0.7, t("exportPanel.stageWriting"));
              if (isObj) {
                filename = `ID${this.project.id}_model${suffix}_obj.zip`;
                files = await buildObjFiles(
                  {
                    object3D: root,
                    glb: sourceGlb,
                    // Only the model's own materials may claim the GLB atlas;
                    // panel textures are rasterized from the live maps.
                    atlasOwners: collectMaterialUuids(root.children[0]),
                  },
                  `ID${this.project.id}_model${suffix}_obj`
                );
              } else {
                filename = `ID${this.project.id}_model${suffix}.glb`;
                files = [
                  { name: filename, blob: await exportGlbBlob(root, { signal }) },
                ];
              }
            } finally {
              root.dispose();
            }

            report(1, t("exportPanel.stageWriting"));
            trackEvent("model_downloaded_ff", {
              project_id: this.project.id,
              file_name: filename,
              format: isObj ? "obj" : "glb",
              includes_pv_arrays: withArrays,
              customer_type: this.currentUser.isEmployee
                ? "employee"
                : "customer",
            });
            return files;
          },
        });
      }

      return items;
    },
  },
  async created() {
    if (this.isFrontendOnlyDemo) {
      setFrontendOnlyDemoRole();
    }
    initViewerColors(this.isBobbieContext ? bobbieTheme : defaultTheme);

    let lang = this.$route.query.lang;

    // Store language preference in localStorage for persistence through auth flow
    if (lang) {
      storeLanguagePreference(lang);
    } else {
      // If no lang parameter in URL, try to get from localStorage
      lang = getStoredLanguagePreference();
    }

    await changeLocale(lang);
    this.loading = true;
    this.$store.commit("setIsThermal", this.thermal ? true : false);
    this.sessionStartTime = new Date();

    if (this.isFrontendOnlyDemo) {
      this.sampleModelName = this.$route.path.substring(1);
    }

    const gpuTier = await getGPUTier();

    await this.setUpProjectAndCompany();

    trackEvent("fast_fusion_solar_entered", {
      page_title: "AirteamViewer",
      page_location: window.location.href,
      page_path: window.location.pathname,
      project_id: this.project.id,
      date: Date.now(),
      customer_type: this.currentUser.isEmployee ? "employee" : "customer",
      gpu_tier: gpuTier.tier,
      gpu_model: gpuTier.gpu,
    });

    this.getCustomPanels({ projectId: this.project.id });
    if (!TEST_COMPANIES.includes(this.company.id)) {
      trackEvent("fast_fusion_solar_entered", {
        page_title: "AirteamViewer",
        page_location: window.location.href,
        page_path: window.location.pathname,
        project_id: this.project.id,
        date: Date.now(),
        customer_type: this.currentUser.isEmployee ? "employee" : "customer",
      });
    }

    this.init();
    this.showBridge(this.project.modelId, this.project.modelPath);
    const clock = new THREE.Clock();
    this.clock = clock;
  },
  mounted() {
    document.querySelector("body").style.paddingTop = "0";
    // Add check for iframe and apply class
    if (window.self !== window.top) {
      document.body.classList.add("within-iframe");
    }

    window.addEventListener("pageshow", this.handlePageShow);
  },
  beforeUnmount() {
    document.querySelector("body").style.paddingTop = null;
    this.removeFirstVisitHintListeners();
    window.removeEventListener("pageshow", this.handlePageShow);

    if (this.fpsResizeObserver) {
      this.fpsResizeObserver.disconnect();
      this.fpsResizeObserver = null;
    }
    if (this.stats && this.stats.dom && this.stats.dom.parentNode) {
      this.stats.dom.parentNode.removeChild(this.stats.dom);
    }
  },
  beforeRouteLeave(to, from, next) {
    this.trackPageView();

    document.removeEventListener("keydown", this.keyDownInit);
    document.removeEventListener("keyup", this.keyUpInit);
    window.removeEventListener("keydown", this.keyDownInitWindow);
    window.removeEventListener("dblclick", this.addZoomCenter, false);
    window.removeEventListener("resize", this.onWindowResize, false);
    window.removeEventListener("blur", this.resetCommandKeyStates, false);

    if (this.active === 0) {
      document.removeEventListener(
        "mousemove",
        this.detectMeasurementIntersection,
        false
      );
    } else if (this.active === 1) {
      document.removeEventListener("click", this.addMarker, false);
      document.removeEventListener(
        "mousemove",
        this.detectModelIntersection,
        false
      );
      document.removeEventListener("keydown", this.deleteSelectedPanel, false);
    } else {
      document.removeEventListener("click", this.addPoint, false);
    }
    // TODO: closeEditMode method doesn't exist, commenting out to fix navigation
    //   this.closeEditMode();
    this.disablePointDragMode();
    next();
  },
  methods: {
    hasPermission,
    ...mapActions(["getCustomPanels", "setNewCustomPanels"]),
    ...Object.fromEntries(
      [
        ...Object.entries(commonMethods),
        ...Object.entries(navigationMethods),
        ...Object.entries(cameraMethods),
        ...Object.entries(measurementMethods),
        ...Object.entries(areaMethods),
        ...Object.entries(restrictedAreaMethods),
        ...Object.entries(panelMethods),
        ...Object.entries(fixedTiltMethods),
        ...Object.entries(eastWestMethods),
        ...Object.entries(areaMeasurementMethods),
        ...Object.entries(galleryMethods),
        ...Object.entries(undoRedoMethods),
      ].map(([key, value]) => [
        key,
        function (...args) {
          return value.apply(this, args);
        },
      ])
    ),
    openExportModal() {
      // Re-checked on every open: panels hang off non-reactive Three.js
      // objects, so nothing would invalidate this on its own.
      this.exportPanelsAvailable =
        this.objectIsloaded &&
        hasExportablePanels(this.areas.map((area) => toRaw(area)));
      this.exportModalOpen = true;
    },
    openCsvVisualizer(stats) {
      this.csvVisualizerStats = stats;
      this.csvVisualizerVisible = true;
    },
    handleSelectAnnotation(ann) {
      this.selectedAnnotationId = ann.id;
      this.openAnnotationInGallery(ann.image_index, ann.id, ann.image_id);
      this.highlightAnnotationMarker(ann.id);
    },
    highlightAnnotationMarker(annotationId) {
      if (!this.annotationMarkers) return;
      this.annotationMarkers.forEach((marker) => {
        const isMatch =
          marker._annotationClickData?.annotationId == annotationId;
        const svg = marker.element?.querySelector("svg");
        if (!svg) return;
        const path = svg.querySelector("path");
        const text = svg.querySelector("text");
        const circle = svg.querySelector("circle");
        const color = isMatch ? "#1677ff" : "#FA6930";
        if (path) {
          path.setAttribute("stroke", color);
          path.setAttribute("fill", isMatch ? "white" : "white");
        }
        if (text) text.setAttribute("fill", color);
        if (circle) circle.setAttribute("fill", isMatch ? "#1677ff" : "white");
      });
    },
    hideAnnotationMarkers() {
      this.annotationMarkersHidden = true;
      this.annotationMarkers?.forEach((marker) => {
        marker.visible = false;
      });
    },
    showAnnotationMarkers() {
      this.annotationMarkersHidden = false;
      this.annotationMarkers?.forEach((marker) => {
        marker.visible = true;
      });
    },
    updateAllAnnotations(newAnnotations) {
      // Creating a new annotation while the section is hidden makes all of them visible again
      if (newAnnotations.length > this.allAnnotations.length) {
        this.$refs.mainSidebaRef?.showAnnotationsFlag();
      }
      this.setAllAnnotations(newAnnotations);
    },

    // The only place allAnnotations is assigned. Its order decides both the
    // sidebar numbers and the marker numbers, so projecting the list and
    // relabelling the markers have to happen together — doing one without the
    // other is what lets the two numberings drift apart.
    setAllAnnotations(items) {
      this.allAnnotations = this.projectableAnnotations(items);
      this.renumberAnnotationMarkers();
    },

    // Deleting drops the damage from the list straight away rather than waiting
    // for the refetch, so the sidebar and the markers close the gap together.
    // The refetch still follows, to pick up anything else that changed.
    async handleDamageDeleted(annotationId) {
      this.setAllAnnotations(
        this.allAnnotations.filter((ann) => ann.id != annotationId)
      );
      await this.refreshDamageAnnotations();
    },
    getRealProjectId(projectId) {
      if (isProduction) {
        if (projectId === "sample_1") return "85817";
        if (projectId === "sample_2") return "85571";
        if (projectId === "sample_3") return "86503";
      } else {
        if (projectId === "sample_1") return "6485";
      }
      return projectId;
    },
    checkIfSharedProjectIsSample() {
      if (isProduction) {
        switch (this.project.id.toString()) {
          case "85817":
            this.project.name = this.$t("sampleProjects.name", { id: 1 });
            this.project.description = this.$t("sampleProjects.description_1");
            this.project.isSample = true;
            break;

          case "85571":
            this.project.name = this.$t("sampleProjects.name", { id: 2 });
            this.project.description = this.$t("sampleProjects.description_2");
            this.project.isSample = true;
            break;

          case "86503":
            this.project.name = this.$t("sampleProjects.name", { id: 3 });
            this.project.description = this.$t("sampleProjects.description_3");
            this.project.isSample = true;
            break;
          default:
            this.project.name = this.project.id;
            this.project.isSample = false;
        }
      } else {
        switch (this.project.id.toString()) {
          case "6485":
            this.project.name = this.$t("sampleProjects.name", { id: 1 });
            this.project.description = this.$t("sampleProjects.description_1");
            this.project.isSample = true;
            break;
          default:
            this.project.name = this.project.id;
            this.project.isSample = false;
        }
      }
    },
    async setUpProjectAndCompany() {
      const token = this.$route?.query?.token;
      if (token) {
        const decodedPayload = decodeJwt(token);
        if (!decodedPayload) {
          authZitadelMethods.noCurrentProject();
          return;
        }
        const glbUrl = decodedPayload["project"]["modelPath"];
        const modelId = glbUrl.split("/").pop();
        const modelPath = glbUrl.replace(modelId, "");
        this.project = {
          ...decodedPayload["project"],
          modelId,
          modelPath,
          technicalCategoryId:
            decodedPayload["project"]["technical_category_id"] ?? null,
          orientation: this.transformOrientation(
            decodedPayload["project"]["orientation"]
          ),
        };
        this.checkIfSharedProjectIsSample();
        this.company = decodedPayload["company"];
        this.hasCalibration = decodedPayload["project"]["orientation"]
          ? true
          : false;
        this.anonymousUser = true;
        setAnonymousRole(decodedPayload["project"]["access_level"]);

        this.projectId = this.project.id;
        this.companyId = this.company?.id;

        if (decodedPayload["project"]["is_bobbie"]) {
          this.$store.commit("setIsBobbieContext", true);
          document.body.classList.add("bobbie-theme");

          const root = document.documentElement;
          root.style.setProperty("--q-primary", bobbieTheme.primary);
          root.style.setProperty("--q-secondary", bobbieTheme.secondary);
          root.style.setProperty("--q-accent", bobbieTheme.accent);
          root.style.setProperty("--q-dark", bobbieTheme.dark);
          root.style.setProperty("--q-positive", bobbieTheme.positive);
          root.style.setProperty("--q-negative", bobbieTheme.negative);
          root.style.setProperty("--q-info", bobbieTheme.info);
          root.style.setProperty("--q-warning", bobbieTheme.warning);

          document.title = "Bobbie 3D Modell";

          const favicon =
            document.querySelector("link[rel~='icon']") ||
            document.createElement("link");
          favicon.rel = "icon";
          favicon.href = "/assets/favicon-bobbie.ico";
          document.head.appendChild(favicon);

          initViewerColors(bobbieTheme);
        }
      } else {
        if (!this.$route.params.projectId && !this.isFrontendOnlyDemo) {
          authZitadelMethods.noCurrentProject();
          return;
        }

        if (!this.isFrontendOnlyDemo) {
          const [projectResponse, projectCategoriesResponse, userResponse] =
            await Promise.all([
              API.projects.getProject(
                this.getRealProjectId(this.$route.params.projectId)
              ),
              API.projectFileCategories.getProjectFileCategories(
                this.getRealProjectId(this.$route.params.projectId)
              ),
              API.users.getCurrentUser(),
              this.$store.dispatch("fetchFeatureFlags"),
            ]);
          const projectData = projectResponse.data;
          const userData = userResponse.data;
          let technicalCategory = projectCategoriesResponse.data.find(
            (category) =>
              category.nameEn === TECHNICAL_PICTURES_CATEGORY.en ||
              category.nameDe === TECHNICAL_PICTURES_CATEGORY.de ||
              category.nameEn === BLURRED_TECHNICAL_PICTURES_CATEGORY.en ||
              category.nameDe === BLURRED_TECHNICAL_PICTURES_CATEGORY.de
          );

          // Check both blurringEnabled attribute
          if (projectData.blurringEnabled) {
            technicalCategory = projectCategoriesResponse.data.find(
              (category) => category.index === BLURRED_CATEGORY_INDEX
            );
            this.usingBlurredImages = true;
          }

          const glbUrl = projectData.fastFusionModel.glb;
          const modelId = glbUrl.split("/").pop();
          const modelPath = glbUrl.replace(modelId, "");
          this.project = {
            id: projectData.pk,
            modelId: modelId,
            modelPath: modelPath,
            flightDate: projectData.flightDate,
            address: projectData.address,
            latitude: projectData.latitude,
            longitude: projectData.longitude,
            orientation: this.transformOrientation(
              projectData.solarModelOrientation
            ),
            scale: projectData.solarModelScale,
            technicalCategoryId: technicalCategory?.pk || null,
            isSampleProject: !userData.isEmployee && projectData.isSample,
            isSample: projectData.isSample,
            solarModeAccessible: projectData.solarModeAccessible ?? true,
          };

          this.checkIfSharedProjectIsSample();

          this.company = {
            id: projectData.company.id,
            logo: projectData.company.logoViewer,
          };

          await this.$store.dispatch(
            "fetchSubscriptionAddons",
            this.company.id
          );

          this.currentUser = {
            id: userData.userPk,
            isEmployee: userData.isEmployee,
            isBobbieUser: userData.isBobbieUser,
          };
          this.$store.commit("setIsEmployee", userData.isEmployee);

          if (this.project.isSampleProject) {
            this.$store.commit("setIsSampleProject", true);
          }

          H.identify(this.project.id, { ...this.project });
        }
        this.projectId = this.project.id;
        this.companyId = this.company.id;
      }
    },

    transformOrientation(orientation) {
      let defaultOrientation = {
        x: -90,
        y: 0,
        z: 0,
      };
      try {
        // Handle both string and object formats
        if (typeof orientation === "string") {
          orientation = JSON.parse(orientation.replace(/'/g, '"'));
        }
        this.hasCalibration = true;
        // Use parseFloat to preserve decimal precision
        const transformedOrientation = {
          x: parseFloat(orientation.x || 0),
          y: parseFloat(orientation.y || 0),
          z: parseFloat(orientation.z || 0),
        };

        this.savedOrientation = { ...transformedOrientation };
        return transformedOrientation;
      } catch (error) {
        console.log("Orientation parsing failed", error);
        // Set default values when parsing fails
        this.hasCalibration = false;
        this.savedOrientation = { ...defaultOrientation };
        return defaultOrientation;
      }
    },

    updateActive(active) {
      this.toggleActive(active);
    },
    distanceMeasurementActivate() {
      if (hasPermission(Permission.Update_Objects)) {
        this.$refs.mainSidebaRef.handleComponentCreation("distance");
      }
    },
    deselectSidebarDistances() {
      if (this.$refs.mainSidebaRef) {
        this.$refs.mainSidebaRef.deselectSidebarDistances();
      }
    },
    colapseComponentsFrom(componentName) {
      if (this.$refs.mainSidebaRef) {
        this.$refs.mainSidebaRef.setColapsedSections(componentName);
      }
    },
    deselectSidebarArea() {
      if (this.$refs.mainSidebaRef) {
        this.$refs.mainSidebaRef.deselectSidebarArea();
      }
    },
    selectMeasurementFromCanvas(indexOfDistance) {
      if (hasPermission(Permission.Update_Objects)) {
        this.$refs.mainSidebaRef.selectMeasurementFromCanvas(indexOfDistance);
      }
    },
    selectMeasurementAreaFromCanvas(indexOfArea) {
      if (hasPermission(Permission.Update_Objects)) {
        this.$refs.mainSidebaRef.selectMeasurementAreaFromCanvas(indexOfArea);
      }
    },
    selectAreaOnSidebar(selectedSolarArea) {
      if (this.$refs.mainSidebaRef) {
        this.$refs.mainSidebaRef.selectAreaOnSidebar(selectedSolarArea);
      }
    },
    unselectSolarAreaOnSidebar() {
      if (this.$refs.mainSidebaRef) {
        this.$refs.mainSidebaRef.unselectSolarAreaOnSidebar();
      }
    },
    async starClicked(panel) {
      this.likePanel(panel);
    },
    solarGroupActivate() {
      this.$refs.mainSidebaRef.handleComponentCreation("solar");
    },
    areaMeasurementActivate() {
      if (hasPermission(Permission.Update_Objects)) {
        this.$refs.mainSidebaRef.handleComponentCreation("area");
      }
    },
    distanceMeasurementActivate() {
      if (hasPermission(Permission.Update_Objects)) {
        this.$refs.mainSidebaRef.handleComponentCreation("distance");
      }
    },
    unselectDistances() {
      this.hideMeasurementDetails();
      this.isMeasurementSelected = false;
      this.deselectSidebarDistances();
    },
    unselectArea() {
      this.unselectMeasurementArea(this.selectedMeasurementArea);
      this.selectedMeasurementArea = null;
      this.detectedMeasurementArea = null;
      this.deselectSidebarArea();
    },
    handleNavigateToOldPlacements() {
      let url = new URL(window.location.href);
      url.hostname = url.hostname.replace("beta.", "");
      window.location.href = url.toString();
    },
    starClicked(panel) {
      this.likePanel(panel);
    },
    async resyncDefaultValuesOnUpdate(updatedDefaultValues) {
      this.solarDefaultValues = updatedDefaultValues;
    },
    async likePanel(panel) {
      this.likedPanel = panel;
      try {
        await API.airteam3DViewer.updateLikedPanel({
          panelId: panel.id,
        });
      } catch (error) {
        console.error("Failed to update custom panel:", error);
      }
    },
    async unlikePanel() {
      this.likedPanel = null;
      this.likedPanelId = null;
      try {
        await API.airteam3DViewer.unlikePanel();
      } catch (error) {
        console.error("Failed to update custom panel:", error);
      }
    },
    updateSelectedArea(area) {
      if (this.active === 2) this.removeUnfinshedArea();
      this.updateActive(2);
      this.selectAreaWithoutClick(area);
    },
    updateSelectedDistance(distanceId) {
      if (this.active !== 0 && this.active !== 1 && this.active !== 4) {
        this.toggleActive(0);
      }
      if (this.active === 1) this.removeUnfinishedMeasurements();
      if (this.selectedMeasurementArea)
        this.unselectFromSidebar(this.selectedMeasurementArea);
      this.showMeasurementFromSidebar(distanceId);
    },
    // The share button used to mint one all-or-nothing link and copy it on the
    // spot. It now opens a dialog instead, because the sharer first has to say
    // how much of the project the link should expose.
    shareSolarModel() {
      this.shareModalOpen = true;
    },
    onModelShared({ method, accessLevel }) {
      if (!TEST_COMPANIES.includes(this.company.id)) {
        trackEvent(
          "share_ffsolar_model",
          {
            event_label: "FFSolar model shared",
            method,
            access_level: accessLevel,
            project_id: this.project.id,
            customer_type: this.currentUser.isEmployee
              ? "employee"
              : "customer",
          },
          // Employees do most of the sharing, so the default filter would drop
          // nearly every one of these and leave the question unanswerable.
          // customer_type still separates internal from customer traffic.
          { includeEmployees: true }
        );
      }
      // Copying leaves the link on the clipboard with nothing on screen to say
      // so, so hand over to the existing confirmation. The email path reports
      // itself and closes, so it needs nothing here.
      if (method === "copy") {
        this.shareModalOpen = false;
        this.dialogVisible = true;
      }
    },
    init() {
      this.container = document.createElement("div");
      this.selectionBox = document.createElement("div");
      this.selectionBox.style.border = "3px solid var(--color-map-green)";
      this.selectionBox.style.position = "absolute";
      this.selectionBox.style.pointerEvents = "none";

      this.$nextTick(() => {
        this.$refs.airteamViewerDiv.appendChild(this.container);
        this.$refs.airteamViewerDiv.appendChild(this.selectionBox);
        this.canvasElementRef = document.querySelector("canvas");
        document.querySelector("canvas").style.height = "100%";
        document.querySelector("canvas").dataset.sl = "canvas-hq";
      });

      /* Scene */
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0x000000);

      this.ambient = new THREE.AmbientLight(WHITE, 3.0);
      this.scene.add(this.ambient);

      this.renderer = new THREE.WebGLRenderer({
        antialias: true,
        powerPreference: "high-performance",
        stencil: true,
      });
      this.renderer.autoClear = false;
      this.currentPixelRatio = window.devicePixelRatio;
      this.renderer.setPixelRatio(Math.min(this.currentPixelRatio, 2));
      this.renderer.setSize(
        window.innerWidth * this.getViewerWidth,
        window.innerHeight
      );
      this.renderer.setClearColor(new THREE.Color("hsl(0, 0%, 10%)"));
      this.renderer.domElement.style.cursor = "grab";

      this.renderer.toneMapping = THREE.NoToneMapping;
      this.renderer.outputEncoding = THREE.sRGBEncoding;

      this.container.appendChild(this.renderer.domElement);

      /* Label Renderer */
      this.labelRenderer = new CSS2DRenderer();
      this.labelRenderer.setSize(
        window.innerWidth * this.getViewerWidth,
        window.innerHeight
      );
      this.labelRenderer.domElement.style.position = "absolute";
      this.labelRenderer.domElement.style.top = "0px";
      this.labelRenderer.domElement.style.pointerEvents = "none";
      // CSS2DRenderer gives every label its own z-index (up to the label
      // count) to sort them by depth. Without a z-index of its own, this
      // container lets those values compete with the rest of the viewer, so a
      // busy model's labels climb over menus and the first-visit overlay.
      // z-index 1 makes it a stacking context: labels still sort among
      // themselves, but the layer as a whole stays just above the canvas.
      this.labelRenderer.domElement.style.zIndex = "1";
      this.container.appendChild(this.labelRenderer.domElement);

      // arrow
      this.arrowScene = new THREE.Scene();
      const light = new THREE.AmbientLight(WHITE, 3.0);
      this.arrowScene.add(light);

      this.arrowRenderer = new THREE.WebGLRenderer({
        alpha: true,
        antialias: true,
      });
      // Matches the main renderer: without a pixel ratio the compass renders one
      // device pixel per CSS pixel and gets upscaled on a retina display, which
      // left the baked letter looking pixelated.
      this.arrowRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      this.arrowRenderer.setSize(COMPASS_CANVAS_PX, COMPASS_CANVAS_PX);
      this.$nextTick(() => {
        const arrowContainer = document.getElementById("arrow-container");
        arrowContainer.appendChild(this.arrowRenderer.domElement);
        document.querySelector("canvas").style.height = "100%";
      });

      this.markers = [];
      this.additionalMarkers = [];

      this.raycaster = new THREE.Raycaster();
      // Only honored by BVH-accelerated meshes (the model); all model raycast
      // call sites consume intersects[0] only. Multi-hit raycasts against
      // planes/markers use the default path and are unaffected.
      this.raycaster.firstHitOnly = true;
      this.mouse = new THREE.Vector2();
      this.spriteMaterial = new THREE.SpriteMaterial({
        map: new THREE.TextureLoader().load(newMarker),
      });
      this.getDefaultPanelTypes().then((allPanelTypes) => {
        allPanelTypes.forEach((type) => {
          if (type.orientation === VERTICAL) {
            this.verticalPanelTypes.push(type);
          } else {
            this.horizontalPanelTypes.push(type);
          }
        });
        this.loadPanelTextures();
      });

      this.frustum = new THREE.Frustum();

      this.dotGeometry = new THREE.SphereGeometry(0.1, 32, 32);
      this.middotGeometry = new THREE.SphereGeometry(0.075, 32, 32);

      this.panelVerticesCache = new Map();

      /* Events */
      window.addEventListener("resize", this.onWindowResize, false);
      window.addEventListener("blur", this.resetCommandKeyStates, false);

      // Initialize stats.js (employee-only; hidden until "Toggle FPS" is on)
      if (this.currentUser.isEmployee) {
        this.stats = new Stats();
        this.stats.showPanel(0); // 0: fps, 1: ms, 2: mb, 3+: custom
        document.body.appendChild(this.stats.dom);

        // Pin to the top, just right of the left panel; left edge computed adaptively.
        this.stats.dom.style.position = "fixed";
        this.stats.dom.style.right = "auto";
        this.stats.dom.style.top = "1rem";
        this.stats.dom.style.display = this.showFps ? "block" : "none";
        this.updateFpsPosition();

        // Re-position when the left panel's size changes (resolution, content, collapse).
        if (typeof ResizeObserver !== "undefined" && this.$refs.leftPanelRef) {
          this.fpsResizeObserver = new ResizeObserver(() =>
            this.updateFpsPosition()
          );
          this.fpsResizeObserver.observe(this.$refs.leftPanelRef);
        }
      }
    },
    initializeCamera() {
      /* Camera */
      this.perspectiveCamera = new THREE.PerspectiveCamera(
        50,
        (window.innerWidth * this.getViewerWidth) / window.innerHeight,
        0.01,
        1000
      );
      this.camera = this.perspectiveCamera;

      const zoomOutFactor = this.calculateModelHeight() * 2;
      const { xFactor, yFactor, zFactor, xTarget, yTarget, zTarget } =
        this.calculateCameraPosition(zoomOutFactor);
      this.camera.position.set(xFactor, yFactor, zFactor);
      this.camera.lookAt(this.modelObject.position);

      this.cameraControls = new CameraControls(
        this.camera,
        this.renderer.domElement
      );

      this.cameraControls.minDistance = 2;
      this.cameraControls.maxDistance = zoomOutFactor * (3 / 2);
      // Zoom towards whatever is under the pointer rather than the orbit
      // target, so scrolling in on a roof detail keeps it under the cursor.
      this.cameraControls.dollyToCursor = true;

      if (xTarget)
        this.cameraControls.setTarget(xTarget, yTarget, zTarget, true);

      this.cameraControls.mouseButtons.left = CameraControls.ACTION.ROTATE;
      this.cameraControls.mouseButtons.middle = CameraControls.ACTION.ROTATE;
      this.cameraControls.mouseButtons.right = CameraControls.ACTION.ROTATE;
    },
    initializeNorthArrowCamera() {
      // Aspect 1 because the compass canvas is square. It used to take the main
      // viewport's aspect, which stretched the little scene horizontally and
      // made the needle change width as the compass turned.
      this.arrowCamera = new THREE.PerspectiveCamera(40, 1, 0.01, 1000);
      this.arrowCamera.position.set(0, COMPASS_CAMERA_DISTANCE, 0);
      this.arrowCamera.lookAt(this.arrowHelper.position);
    },
    toggleCameraMode() {
      const currentPosition = this.camera.position.clone();
      const currentTarget = new THREE.Vector3();
      this.cameraControls.getTarget(currentTarget);

      const aspect =
        (window.innerWidth * this.getViewerWidth) / window.innerHeight;

      if (this.isOrthographicCamera) {
        this.camera = this.perspectiveCamera;
        this.isOrthographicCamera = false;
      } else {
        const frustumSize = currentPosition.distanceTo(currentTarget);
        if (!this.orthographicCamera) {
          this.orthographicCamera = new THREE.OrthographicCamera(
            (frustumSize * aspect) / -2,
            (frustumSize * aspect) / 2,
            frustumSize / 2,
            frustumSize / -2,
            0.01,
            1000
          );
        } else {
          this.orthographicCamera.left = (frustumSize * aspect) / -2;
          this.orthographicCamera.right = (frustumSize * aspect) / 2;
          this.orthographicCamera.top = frustumSize / 2;
          this.orthographicCamera.bottom = frustumSize / -2;
          this.orthographicCamera.updateProjectionMatrix();
        }
        this.camera = this.orthographicCamera;
        this.isOrthographicCamera = true;
      }

      this.camera.position.copy(currentPosition);
      this.camera.lookAt(currentTarget);

      this.cameraControls.dispose();
      this.cameraControls = new CameraControls(
        this.camera,
        this.renderer.domElement
      );

      const zoomOutFactor = this.calculateModelHeight() * 2;
      this.cameraControls.minDistance = 2;
      this.cameraControls.maxDistance = zoomOutFactor * (3 / 2);
      // Matches the initial setup: zoom follows the cursor.
      this.cameraControls.dollyToCursor = true;
      this.cameraControls.setTarget(
        currentTarget.x,
        currentTarget.y,
        currentTarget.z,
        false
      );

      this.cameraControls.mouseButtons.left = CameraControls.ACTION.ROTATE;
      this.cameraControls.mouseButtons.middle = CameraControls.ACTION.ROTATE;
      this.cameraControls.mouseButtons.right = CameraControls.ACTION.ROTATE;

      if (this.transformControls) {
        this.transformControls.camera = this.camera;
      }
    },
    renderScene() {
      this.initializeCamera();

      document.addEventListener("keydown", this.keyDownInit);

      document.addEventListener("keyup", this.keyUpInit);

      document.addEventListener("keydown", this.escClicked, false);
      document.addEventListener("keydown", this.hotKeyClicked, false);

      window.addEventListener("keydown", this.keyDownInitWindow);

      window.addEventListener("dblclick", this.addZoomCenter, false);

      window.addEventListener("wheel", this.hideAuxiliaryValuesOnZoom, false);

      this.animate();
    },
    onProgress(event) {
      // q-linear-progress takes a 0..1 fraction, so keep the ratio unrounded —
      // rounding it collapsed the bar to only ever showing 0 or 1.
      if (!event.lengthComputable || !event.total) return;
      this.loadingProgress = event.loaded / event.total;
    },
    async showModel(modelName, baseUrl) {
      // No cache-buster on purpose: the backend already version-addresses the
      // model (".../files/<fileId>/gltf_files/<timestamp>_<fileId>.glb"), so a
      // re-export arrives as a new URL. Appending a random value defeated the
      // bucket's `cache-control: max-age=3600` and re-downloaded ~10 MB on
      // every open. Past max-age the browser revalidates with the GCS ETag.
      this.loadedModelUrl = (baseUrl || "") + modelName;
      const loader = new GLTFLoader();
      loader.setPath(baseUrl);
      loader.load(
        modelName,
        async (gltf) => {
          this.modelObject = gltf.scene;
          this.buildModelBoundsTree();
          this.exportedManually =
            this.modelObject.children[0].userData?.exported_manually;
          this.fixModelRotation();
          if (this.exportedManually && this.project.orientation.x === -90) {
            this.project.orientation.x = 90;
          }
          this.orientationUpdated();
          this.scalingUpdated(this.project.scale, true);
          this.renderScene();
          this.scene.add(gltf.scene);
          if (!this.isFrontendOnlyDemo) {
            await this.loadPanelTextures();
            if (hasPermission(Permission.View_Annotations)) {
              await this.displayExistingModelObjects();
            }
            this.objectIsloaded = true;
            if (hasPermission(Permission.View_PersonalSettings)) {
              await Promise.all([
                this.getUserDefaultSolarValues(),
                this.getUserLikedPanel(),
              ]);
              this.applyStoredViewerTheme();
            }
          }
          this.initializeTransformControls();
          this.toggleActive(0);
          this.loading = false;
          this.onWindowResize();
          this.showNavigationHintIfDue();

          if (this.bannerKey === "sample") {
            return ElMessage.warning({
              message: this.$t("sampleProjects.warning"),
              duration: 0,
            });
          }
          if (this.bannerKey === "viewer") {
            return ElMessage.warning({
              message: this.$t("viewerMode.warning"),
              duration: 0,
            });
          }
        },
        this.onProgress
      );
    },
    createGradientBackgroundTexture() {
      const canvas = document.createElement("canvas");
      canvas.width = 1;
      canvas.height = 256;
      const ctx = canvas.getContext("2d");
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, "#569ACC");
      gradient.addColorStop(0.5, "#C0D7E6");
      gradient.addColorStop(1, "#E5EEEF");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      return texture;
    },
    setViewerTheme(lightMode) {
      this.isLightMode = lightMode;
      this.applyCompassTheme();
      if (lightMode) {
        if (!this.gradientBackgroundTexture) {
          this.gradientBackgroundTexture =
            this.createGradientBackgroundTexture();
        }
        this.scene.background = this.gradientBackgroundTexture;
      } else {
        this.scene.background = new THREE.Color(0x000000);
      }
    },
    async toggleViewerTheme(lightMode) {
      this.setViewerTheme(lightMode);
      if (!this.syncThemeWithBackend) return;
      try {
        await API.airteam3DViewer.updateDefaultValues({
          viewer_light_mode: lightMode,
        });
      } catch {
        // Preference is best-effort: the theme still applies for this session.
      }
    },
    // Whether this visitor's hint lives in their backend settings. Mirrors the
    // conditions under which the model load fetches those settings; everyone
    // else -- sample models, shared links, and those embedded in an iframe --
    // has none, and keeps it in this browser instead.
    navigationHintUsesBackend() {
      return (
        !this.isFrontendOnlyDemo &&
        hasPermission(Permission.View_PersonalSettings)
      );
    },
    // First visit: show the rotate hint over the canvas. On the backend path it
    // checks for an explicit false, so settings without the field (an older
    // backend) never trigger it.
    showNavigationHintIfDue() {
      const due = this.navigationHintUsesBackend()
        ? this.solarDefaultValues?.navigation_hint_seen === false
        : !hasSeenNavigationHintLocally();
      if (!due) return;
      this.showFirstVisitHint = true;
      // The overlay sits over the toolbars as well as the canvas, so the
      // first press or scroll anywhere dismisses it -- captured, so a click on
      // a toolbar button counts even if the button stops the event.
      window.addEventListener("pointerdown", this.dismissFirstVisitHint, true);
      window.addEventListener("wheel", this.dismissFirstVisitHint, {
        capture: true,
        passive: true,
      });
    },
    dismissFirstVisitHint() {
      if (!this.showFirstVisitHint) return;
      this.showFirstVisitHint = false;
      this.removeFirstVisitHintListeners();
      this.markNavigationHintSeen();
    },
    removeFirstVisitHintListeners() {
      window.removeEventListener("pointerdown", this.dismissFirstVisitHint, true);
      window.removeEventListener("wheel", this.dismissFirstVisitHint, true);
    },
    async markNavigationHintSeen() {
      if (!this.navigationHintUsesBackend()) {
        markNavigationHintSeenLocally();
        return;
      }
      this.solarDefaultValues.navigation_hint_seen = true;
      try {
        await API.airteam3DViewer.updateDefaultValues({
          navigation_hint_seen: true,
        });
      } catch {
        // Best-effort: at worst the guide opens once more on the next visit.
      }
    },
    applyStoredViewerTheme() {
      if (!this.syncThemeWithBackend) return;
      if (this.solarDefaultValues?.viewer_light_mode) this.setViewerTheme(true);
    },
    buildModelBoundsTree() {
      this.modelObject.traverse((child) => {
        if (child.isMesh && child.geometry) {
          child.geometry.boundsTree = new MeshBVH(child.geometry);
          child.raycast = acceleratedRaycast;
        }
      });
    },
    calculateModelHeight() {
      let modelHeight = 50;

      this.modelObject.traverse((child) => {
        if (child.isMesh) {
          const geometry = child.geometry;

          geometry.computeBoundingBox();
          const boundingBox = geometry.boundingBox;
          const minY = boundingBox.min.y;
          const maxY = boundingBox.max.y;

          const height = maxY - minY;

          if (height) modelHeight = height;
        }
      });

      // TO BE REPLACED DURING GOOGLE TILES PROJECT
      if (this.projectId == 54171) modelHeight = 50;

      return modelHeight * this.project.scale;
    },
    updateFpsPosition() {
      if (!this.stats) return;
      const FPS_GAP_PX = 12;
      const FPS_FALLBACK_LEFT_PX = 60;
      const panel = this.$refs.leftPanelRef;
      const rightEdge =
        panel && panel.offsetWidth > 0
          ? panel.getBoundingClientRect().right
          : FPS_FALLBACK_LEFT_PX;
      this.stats.dom.style.left = `${rightEdge + FPS_GAP_PX}px`;
    },
    onWindowResize() {
      this.camera.aspect =
        (window.innerWidth * this.getViewerWidth) / window.innerHeight;
      this.camera.updateProjectionMatrix();

      this.renderer.setSize(
        window.innerWidth * this.getViewerWidth,
        window.innerHeight
      );
      this.labelRenderer.setSize(
        window.innerWidth * this.getViewerWidth,
        window.innerHeight
      );

      this.updateFpsPosition();
    },
    animate() {
      if (this.stats) {
        this.stats.begin(); // Start measuring
      }

      requestAnimationFrame(this.animate);
      const delta = this.clock.getDelta();
      this.cameraControls.update(delta);
      this.updateNorthArrow();
      this.updateAllReactivePoints();
      this.updateAllInstancedMeshPoints();
      this.refreshSelectedSolarGroupLabels();
      this.render();
      H.snapshot(this.canvasElementRef);

      if (this.stats) {
        this.stats.end(); // End measuring
      }
    },
    render() {
      this.labelRenderer.render(this.scene, this.camera);
      this.renderer.render(this.scene, this.camera);
      this.arrowRenderer.render(this.arrowScene, this.arrowCamera);
    },
    async showBridge(modelName, baseUrl) {
      if (!this.isFrontendOnlyDemo)
        baseUrl = baseUrl[baseUrl.length - 1] === "/" ? baseUrl : baseUrl + "/";
      else {
        baseUrl = window.runtimeConfig?.STATIC_MODELS_URL;
        modelName = `${this.sampleModelName}.glb`;
      }

      await this.showModel(modelName, baseUrl);
      await this.loadNorthArrow();
    },
    toggleActive(value, fromPanOrbit = false) {
      const solarModes = [2, 3, 6];
      if (solarModes.includes(value) && !this.showSolarTools) {
        return;
      }
      // AI is a separate flag rather than one of the numbered modes, so
      // picking any tool has to switch it off explicitly — otherwise it stays
      // active alongside the new mode. Damage mode is the same kind of flag:
      // activateDamageMode sets it after its own toggleActive(0) call, which is
      // what keeps this line from undoing it.
      this.closeAiPanel();
      this.damageModeActive = false;
      this.updateMeasurementLabelCursors(value);

      if (this.active === 5 && value !== 5 && this.camerasDisplayed) {
        this.changeCamerasVisibility(false);
      }
      if (value != this.active) {
        if (this.isMeasurementSelected) {
          this.unselectDistances();
        }
        if (this.selectedMeasurementArea) {
          this.unselectArea();
        }
        if (this.selectedArea && value !== 3 && value !== 6) {
          this.unselectSolarAreaOnSidebar();
        }
        if (value === 5) {
          this.removeUnfinishedMeasurements();
          this.unselectDistances();
        }
      }

      if (value !== 1 && value !== 4) this.measurementModeActive = false;
      if (value === 1 || value === 4) {
        this.measurementModeActive = true;
        this.measurementMenuExpanded = true;
      }
      if (
        (value === 1 || value === 2 || value === 4) &&
        hasPermission(Permission.Update_Objects)
      ) {
        this.$refs.mainSidebaRef.handleModeChange(value);
      }

      this.toggleView(value, fromPanOrbit);
    },

    escClicked(event) {
      const key = event.key;

      // The dialog owns Escape while it is up, so it closes rather than
      // deselecting measurements behind it.
      if (this.shareModalOpen) {
        if (key === "Escape") this.shareModalOpen = false;
        return;
      }

      if (key === "Escape") {
        // Check for measurement deselection when active is 0 or 1
        if (
          (this.active === 0 || this.active === 1) &&
          this.isMeasurementSelected
        ) {
          this.unselectDistances();
        }
        // Check for measurement area deselection when active is 0 or 4
        else if (
          (this.active === 0 || this.active === 4) &&
          this.selectedMeasurementArea
        ) {
          this.unselectArea();
        }
        // Handle other cases
        else if (this.active === 1) {
          this.removeUnfinishedMeasurements();
        } else if (this.active === 2) {
          this.removeUnfinshedArea();
          this.unselectSolarArea();
        } else if (this.active === 3) {
          this.disableIndividualPlacementMode();
          if (
            this.selectedArea &&
            this.selectedArea.restrictedAreas.length > 0
          ) {
            this.showRestrictedAreaDetails(this.selectedArea);
          }
        } else if (this.active === 4) {
          this.removeUnfinishedAreaMeasurements();
        } else if (this.active === 6) {
          this.disableRestrictedAreaMode();
        }
      }
    },
    hotKeyClicked(event) {
      const key = event.key;

      if (this.fullScreenImageActive || this.keyboardShortcutsSuspended) {
        return;
      }
      const el = document.activeElement;
      if (
        el &&
        (el.tagName === "TEXTAREA" ||
          el.tagName === "INPUT" ||
          el.isContentEditable)
      ) {
        return;
      }
      if (key === "s") {
        if (this.active === 2 && this.selectedArea) {
          this.toggleActive(6);
        } else if (this.active === 6) {
          this.disableRestrictedAreaMode();
        }
      } else if (key === "e") {
        if (this.active === 2 && this.selectedArea) {
          this.enableIndividualPlacementMode("VERTICAL");
        } else if (this.active === 3) {
          this.disableIndividualPlacementMode();
        }
      } else if (key === "w") {
        if (this.active === 3) {
          this.toggleIndividualPanelOrientation();
        }
      } else if (key === "d") {
        this.toggleActive(1);
      } else if (key === "f") {
        this.toggleActive(4);
      }
    },
    toggleHotkeys(enabled) {
      if (!enabled) {
        document.removeEventListener("keydown", this.hotKeyClicked);
      } else {
        document.addEventListener("keydown", this.hotKeyClicked);
      }
    },
    toggleView(view, fromPanOrbit = false) {
      this.cleanNavigationSetup();
      this.resetUndoStack();
      this.resetRedoStack();

      this.modes.forEach((mode) => {
        if (mode.enabled && view !== 3 && view !== 6) {
          mode.disableFunction.call(this, view);
          if (mode.value === 3 || mode.value === 6) {
            this.modes.find((mode) => mode.value === 2).disableFunction();
          }
        }
      });

      if (this.active === 3 || this.active === 6) {
        this.modes.find((mode) => mode.value === this.active).disableFunction();
      }

      this.active = view;

      const currentMode = this.modes.find((mode) => mode.value === view);

      if (currentMode) {
        currentMode.enableFunction.call(this, fromPanOrbit);
      }
      if (view === null || view === 0) {
        this.modes.find((mode) => mode.value === 2).disableFunction();
      }
    },

    closeDragElement() {
      document.onmouseup = null;
      document.onmousemove = null;
    },
    toggleSidebar(event) {
      this.sidebarOpen = !this.sidebarOpen;
      this.onWindowResize();
      event.stopPropagation();
    },

    async sendAiInstruction(instructions) {
      const imageBase64 = await this.captureAiScreenshot();
      const { data } = await API.airteam3DViewer.editBuildingImage(
        imageBase64,
        instructions
      );
      if (data?.imageBase64) {
        this.aiPreviewImage = data.imageBase64;
      }
      return data;
    },

    async captureAiScreenshot() {
      if (this.aiPreviewImage) {
        return await this.reencodeAsJpeg(this.aiPreviewImage, 1600, 0.85);
      }
      this.renderer.render(this.scene, this.camera);
      return this.renderer.domElement.toDataURL("image/jpeg", 0.9);
    },

    reencodeAsJpeg(dataUrl, maxDimension, quality) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const { naturalWidth: w, naturalHeight: h } = img;
          const scale = Math.min(1, maxDimension / Math.max(w, h));
          const canvas = document.createElement("canvas");
          canvas.width = Math.round(w * scale);
          canvas.height = Math.round(h * scale);
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL("image/jpeg", quality));
        };
        img.onerror = () => reject(new Error("Failed to decode preview image"));
        img.src = dataUrl;
      });
    },

    clearAiPreview() {
      this.aiPreviewImage = null;
    },

    toggleAiPanel() {
      if (this.aiPanelOpen) {
        this.closeAiPanel();
      } else {
        // AI is one of the modes now, so picking it has to drop the active
        // tool just as picking a tool drops AI. toggleActive runs while the
        // panel is still closed, which makes its own closeAiPanel() call a
        // no-op rather than undoing the line after it.
        this.toggleActive(0);
        this.aiPanelOpen = true;
      }
      if (!this.sidebarOpen) {
        this.sidebarOpen = true;
        this.onWindowResize();
      }
    },
    // Leaves AI mode and discards its 2D preview. Safe to call when it is
    // already closed, which is why toggleActive can call it unconditionally.
    closeAiPanel() {
      if (!this.aiPanelOpen) return;
      this.clearAiPreview();
      this.aiPanelOpen = false;
    },

    // The damage detection is opt-in per project: the first click asks, every
    // later one goes straight in. The confirmation is the only place the flag
    // is ever set, so a project that never opts in never sees an AI damage.
    handleDamageModeClick() {
      if (this.damageModeActive) {
        this.damageModeActive = false;
        return;
      }
      if (!this.aiDamagesOptedIn) {
        this.damageNoDamagesFound = false;
        this.damageReviewed = false;
        this.damageModalOpen = true;
        return;
      }
      this.activateDamageMode();
      // Clicking the tool is a question, and "none" is one of its answers —
      // worth saying every time, not only on the click that switched it on.
      if (this.reportDamagesFound()) this.reportScanReviewed();
    },

    // Opted in before this page load: say once per session that the scan was reviewed.
    reportScanReviewed() {
      if (this.damageReviewedNoticeShownFor === this.project.id) return;
      this.damageReviewedNoticeShownFor = this.project.id;
      this.damageReviewed = true;
      this.damageModalOpen = true;
    },

    // Whether the detection has anything to show, raising the "nothing found"
    // notice when it does not and taking the dialog down when it does.
    reportDamagesFound() {
      const found = this.allAnnotations.some(
        (annotation) => annotation.automatically_evaluated
      );
      const announce =
        !found && this.damageNoDamagesNoticeShownFor !== this.project.id;
      if (announce) this.damageNoDamagesNoticeShownFor = this.project.id;
      this.damageNoDamagesFound = announce;
      this.damageModalOpen = announce;
      return found;
    },

    async confirmDamageDetection() {
      if (this.damageOptInLoading) return;
      this.damageOptInLoading = true;
      try {
        await this.optInToAiDamages();
        this.damageReviewedNoticeShownFor = this.project.id;
        await this.reloadAnnotations();
        this.activateDamageMode();
        // Closes the dialog, or turns it into the "nothing found" notice.
        this.reportDamagesFound();
      } catch (e) {
        console.error("Could not load the detected damages:", e);
        ElMessage.error({message: this.$t("damageDetection.failed")});
      } finally {
        this.damageOptInLoading = false;
      }
    },

    // The whole config is sent back: the backend rebuilds the document from the
    // payload, so a partial PUT would reset the visibility flags to their
    // defaults. Persisting needs an edit right the viewer role does not have —
    // without it the opt-in still applies, just only for this session.
    async optInToAiDamages() {
      // Only *that* the mode is being switched on is ours to say. The backend
      // replaces this with its own clock, the one the annotations' dateAdded
      // comes from, and the damage order is decided by comparing the two — so
      // the stored answer is what has to end up in the store, not what we sent.
      const config = {
        ...this.sections.config,
        damage_detection_mode_activated_at: new Date().toISOString(),
      };
      if (!hasPermission(Permission.Update_Objects)) {
        this.$store.dispatch("section/setConfig", config);
        return;
      }
      const response = await API.airteam3DViewer.putProjectConfig(
        this.project.id,
        config
      );
      this.$store.dispatch("section/setConfig", response?.data ?? config);
    },

    // Damage mode is a flag beside the numbered tools, like AI mode: it drops
    // the active tool, opens the sidebar and un-hides the markers, which start
    // hidden. The Damages section itself is always in the sidebar.
    activateDamageMode() {
      this.toggleActive(0);
      this.damageModeActive = true;
      if (!this.sidebarOpen) {
        this.sidebarOpen = true;
        this.onWindowResize();
      }
      this.$refs.mainSidebaRef?.showAnnotationsFlag();
    },

    // Re-reads the annotations and rebuilds every marker from scratch: a marker
    // is placed once per id, so the newly revealed AI damages would otherwise be
    // projected onto the existing ones rather than into their sorted places.
    async reloadAnnotations() {
      const response = await annotationsApi.getAnnotations(this.project.id);
      const items = response.data?.results ?? response.data ?? [];
      this.clearAnnotationMarkers();
      if (items.length > 0) {
        await this.ensureCameraFrustumsForAnnotations();
      }
      this.setAllAnnotations(items);
    },

    async takeScreenshot() {
      if (this.active !== 0) return;
      let img = new Image();
      if (this.aiPreviewImage) {
        img.src = this.aiPreviewImage;
      } else {
        this.renderer.render(this.scene, this.camera);
        img.src = this.renderer.domElement.toDataURL();
      }
      // Read before the assignment below: a screenshot already on the project
      // means this one replaces it.
      const isOverwrite = Boolean(this.screenshot);
      this.screenshot = img;

      let copiedToClipboard = false;
      try {
        const res = await fetch(img.src);
        const blob = await res.blob();
        await navigator.clipboard.write([
          new ClipboardItem({ [blob.type]: blob }),
        ]);
        copiedToClipboard = true;
      } catch (e) {
        // clipboard requires user gesture + HTTPS; the screenshot itself is
        // still saved, so only the clipboard half of the message is dropped
      }

      // Only promise the clipboard when the write actually went through.
      const messageKey = isOverwrite
        ? copiedToClipboard
          ? "overwriteCopied"
          : "overwrite"
        : copiedToClipboard
          ? "successCopied"
          : "success";

      ElMessage.success({
        message: this.$t(`screenshot.${messageKey}`),
        showClose: true,
        duration: "2500",
      });
    },
    async captureTopDownScreenshot() {
      const pos = this.camera.position.clone();
      const target = new THREE.Vector3();
      this.cameraControls.getTarget(target);

      // Same framing as the top view in navigation.js. setLookAt only
      // registers the move — cameraControls.update() applies it to the
      // camera, so it must run before rendering the capture.
      const modelHeight = this.calculateModelHeight() * 2;
      const center = this.modelObject.position;
      this.cameraControls.setLookAt(
        center.x,
        modelHeight,
        center.z,
        center.x,
        center.y,
        center.z,
        false
      );
      this.cameraControls.update(0);
      this.renderer.render(this.scene, this.camera);

      const glCanvas = this.renderer.domElement;
      const composite = document.createElement("canvas");
      composite.width = glCanvas.width;
      composite.height = glCanvas.height;
      const ctx = composite.getContext("2d");
      ctx.drawImage(glCanvas, 0, 0);
      this.drawAnnotationMarkersOnCanvas(
        ctx,
        composite.width,
        composite.height
      );
      const url = composite.toDataURL("image/jpeg", 0.9);

      // Restore
      this.cameraControls.setLookAt(
        pos.x,
        pos.y,
        pos.z,
        target.x,
        target.y,
        target.z,
        false
      );
      this.cameraControls.update(0);
      this.renderer.render(this.scene, this.camera);

      return url;
    },
    drawAnnotationMarkersOnCanvas(ctx, width, height) {
      if (!this.annotationMarkers?.length) return;
      this.camera.updateMatrixWorld(true);
      const ndc = new THREE.Vector3();
      this.annotationMarkers.forEach((marker) => {
        if (!marker.visible) return;
        ndc.copy(marker.position).project(this.camera);
        // Skip markers behind the camera or outside the view frustum.
        if (ndc.z > 1 || ndc.x < -1 || ndc.x > 1 || ndc.y < -1 || ndc.y > 1)
          return;
        const x = (ndc.x * 0.5 + 0.5) * width;
        const y = (-ndc.y * 0.5 + 0.5) * height;
        const annotationId = marker._annotationClickData?.annotationId;
        const index = this.allAnnotations.findIndex(
          (a) => a.id == annotationId
        );
        const label = String((index >= 0 ? index : 0) + 1).padStart(2, "0");
        this.drawAnnotationPin(ctx, x, y, label);
      });
    },
    drawAnnotationPin(ctx, x, y, label) {
      // Scale the pin to the screenshot resolution so it stays legible in the PDF.
      const r = Math.max(14, ctx.canvas.width * 0.012);
      ctx.save();
      // Teardrop pointer.
      ctx.beginPath();
      ctx.moveTo(x - r * 0.55, y - r * 0.35);
      ctx.lineTo(x + r * 0.55, y - r * 0.35);
      ctx.lineTo(x, y + r * 0.6);
      ctx.closePath();
      ctx.fillStyle = "#FA6930";
      ctx.fill();
      // Head circle.
      ctx.beginPath();
      ctx.arc(x, y - r, r, 0, Math.PI * 2);
      ctx.fillStyle = "#FFFFFF";
      ctx.fill();
      ctx.lineWidth = Math.max(2, r * 0.18);
      ctx.strokeStyle = "#FA6930";
      ctx.stroke();
      // Number.
      ctx.fillStyle = "#FA6930";
      ctx.font = `700 ${Math.round(r)}px sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(label, x, y - r);
      ctx.restore();
    },
    getPanelsDataPerSize() {
      const sizeToPanelNumber = {};
      const sizeToPanelSurface = {};

      const addPanels = (width, height, count) => {
        if (!count) return;
        const size = `${Math.round(width * 1000)} x ${Math.round(
          height * 1000
        )} mm`;
        sizeToPanelNumber[size] = (sizeToPanelNumber[size] ?? 0) + count;
        sizeToPanelSurface[size] =
          (sizeToPanelSurface[size] ?? 0) + width * height * count;
      };

      const countVisible = (mesh) => {
        if (!mesh?.visible) return 0;
        let visible = 0;
        for (let i = 0; i < mesh.instancesCount; i++) {
          if (mesh.getVisibilityAt(i)) visible += 1;
        }
        return visible;
      };

      for (const area of this.areas.filter((a) => a.show !== false)) {
        // Legacy areas keep individual panel objects.
        for (const panel of area.panels ?? []) {
          const { width, height } = panel.plane.geometry.parameters;
          addPanels(width, height, 1);
        }
        // Instanced areas: panel dimensions live on the mode's test panel.
        if (area.testPanel) {
          const { width, height } = area.testPanel.geometry.parameters;
          const count = this.getAreaMeshes(area).reduce(
            (total, mesh) => total + countVisible(mesh),
            0
          );
          addPanels(width, height, count);
        }
        if (area.verticalTestPanel) {
          const { width, height } = area.verticalTestPanel.geometry.parameters;
          addPanels(
            width,
            height,
            countVisible(area.verticalPanelInstancedMesh)
          );
        }
        if (area.horizontalTestPanel) {
          const { width, height } =
            area.horizontalTestPanel.geometry.parameters;
          addPanels(
            width,
            height,
            countVisible(area.horizontalPanelInstancedMesh)
          );
        }
      }

      return [sizeToPanelNumber, sizeToPanelSurface];
    },
    /**
     * `ID<project>_<Report Name>.<ext>`, with whitespace collapsed to
     * underscores and path separators stripped — the display names are
     * translated, so they can carry spaces or (in German) a slash.
     */
    exportFilename(name, extension) {
      const safeName = String(name)
        .trim()
        .replace(/[\\/]+/g, "-")
        .replace(/\s+/g, "_");
      return `ID${this.project.id}_${safeName}.${extension}`;
    },
    async buildReportHtml() {
      let sizeToPanelNumberUl =
        this.$i18n.locale === "de"
          ? "Anzahl der Solarmodule pro Größe: <ul>"
          : "Amount of solar panels per size: <ul>";
      let sizeToPanelSurfaceUl =
        this.$i18n.locale === "de"
          ? "Fläche pro Größe: <ul>"
          : "Surface per size: <ul>";
      const [sizeToPanelNumber, sizeToPanelSurface] =
        this.getPanelsDataPerSize();

      let totalPitchAngle = 0;
      let totalSurfaceArea = 0;
      for (let area of this.areas.filter(
        (area) => area.closed && area.show !== false
      )) {
        const surfaceArea = this.calculateSurfaceArea(area);
        totalPitchAngle += surfaceArea * area.angle;
        totalSurfaceArea += Number(surfaceArea);
      }
      const averagePitchAngle = (totalPitchAngle / totalSurfaceArea).toFixed(2);

      Object.entries(sizeToPanelNumber).forEach(([size, panelNumber]) => {
        sizeToPanelNumberUl += `<li>${size}: ${panelNumber}</li>`;
      });
      sizeToPanelNumberUl += "</ul>";

      Object.entries(sizeToPanelSurface).forEach(([size, panelSurface]) => {
        sizeToPanelSurfaceUl += `<li>${size}: ${panelSurface.toFixed(
          2
        )}m²</li>`;
      });
      sizeToPanelSurfaceUl += "</ul>";

      const project = this.$i18n.locale === "de" ? "Projekt" : "Project";
      const numberOfSolarPanels =
        this.$i18n.locale === "de"
          ? "Anzahl der Solarmodule"
          : "Number of solar panels";
      const totalPanelSurface =
        this.$i18n.locale === "de"
          ? "Gesamtfläche der Solarmodule"
          : "Total solar panel surface";
      const averagePitchAngleTitle =
        this.$i18n.locale === "de"
          ? "Durchschnittlicher Neigungswinkel des Bereichs"
          : "Average area pitch angle";

      const pitchAngleTitle = (id) =>
        this.$i18n.locale === "de"
          ? `Bereich ${id} Steigungswinkel`
          : `area ${id} pitch angle`;

      const pitchAnglePerArea =
        this.$i18n.locale === "de"
          ? "Neigungswinkel pro Fläche"
          : "Pitch angle per area";

      const h4 = this.isFrontendOnlyDemo
        ? "Sample Project"
        : `${project} Id: ${this.project.id}`;
      let printData = `<h4>${h4}</h4><p>${numberOfSolarPanels}: ${this.totalNumberOfPanels}</p><p>${totalPanelSurface}: ${this.totalPanelsArea}m²</p><p>${averagePitchAngleTitle}: ${averagePitchAngle}°</p>`;
      printData += sizeToPanelNumberUl;
      printData += sizeToPanelSurfaceUl;

      const closedAreas = this.areas.filter(
        (area) => area.closed && area.show !== false
      );
      if (closedAreas.length > 0)
        printData += `<p>${pitchAnglePerArea}:</p><ul>`;
      for (let i = 0; i < closedAreas.length; i++) {
        printData += `<li>${pitchAngleTitle(i + 1)}: ${
          closedAreas[i].angle
        }°</li>`;
      }
      if (closedAreas.length > 0) printData += "</ul>";
      if (this.screenshot)
        printData += `<div style="page-break-inside: avoid;">${await sizedImgTag(
          this.screenshot.src,
          500
        )}</div>`;

      return printData;
    },
    /**
     * Damage annotations as a standalone report. Split out of the PV report so
     * each PDF only carries what its name promises; the top-down map comes
     * along because it exists to locate the damages, not the arrays.
     * Callers must check allAnnotations themselves — this returns "" when there
     * is nothing to report rather than producing a header with no content.
     */
    async buildDamageReportHtml() {
      if (this.allAnnotations.length === 0) return "";

      const project = this.$i18n.locale === "de" ? "Projekt" : "Project";
      const annotationsTitle =
        this.$i18n.locale === "de" ? "Schäden" : "Damages";
      const h4 = this.isFrontendOnlyDemo
        ? "Sample Project"
        : `${project} Id: ${this.project.id}`;
      let printData = `<h4>${h4}</h4>`;

      try {
        const topDownUrl = await this.captureTopDownScreenshot();
        printData += `<div style="page-break-inside: avoid;">${await sizedImgTag(
          topDownUrl,
          600,
          "margin:20px 0;"
        )}</div>`;
      } catch (e) {
        console.warn("Could not capture top-down screenshot:", e);
      }

      // Load each crop image up front; a card whose image is unavailable
      // renders without it, like an annotation that never had one.
      const cropTags = new Map();
      await Promise.all(
        this.allAnnotations
          .filter((ann) => ann.frame_image)
          .map(async (ann) => {
            try {
              cropTags.set(
                ann.id,
                await sizedImgTag(ann.frame_image, 200, "margin-top:8px;")
              );
            } catch (e) {
              console.warn(
                `Could not load crop image for annotation ${ann.id}:`,
                e
              );
            }
          })
      );
      const annotationCards = this.allAnnotations.map(
        (ann, i) => `
          <div style="margin:20px 0; page-break-inside: avoid; border: 1px solid #eee; padding: 12px;">
            <strong>${String(i + 1).padStart(2, "0")}. ${
          ann.damage_type || ""
        }</strong>
            <br/>Severity: ${ann.severity_level ?? ""}/5
            <br/>Description: ${ann.description || ""}
            <br/>Recommended action: ${ann.recommended_action || ""}
            ${cropTags.has(ann.id) ? `<br/>${cropTags.get(ann.id)}` : ""}
          </div>
        `
      );
      const annotationsTitleHtml = `<h3 style="page-break-after: avoid; margin-bottom: 8px;">${annotationsTitle}</h3>`;
      printData += `<div style="page-break-inside: avoid;">${annotationsTitleHtml}${
        annotationCards[0] ?? ""
      }</div>`;
      printData += annotationCards.slice(1).join("");

      return printData;
    },
    getVerticalComponent(distance) {
      const verticalFirstPoint = distance.firstPoint.position.clone();
      const verticalSecondPoint = new THREE.Vector3(
        distance.firstPoint.position.x,
        distance.secondPoint.position.y,
        distance.firstPoint.position.z
      );
      return verticalSecondPoint.distanceTo(verticalFirstPoint);
    },
    getHorizontalComponent(distance) {
      const horizontalFirstPoint = new THREE.Vector3(
        distance.firstPoint.position.x,
        distance.secondPoint.position.y,
        distance.firstPoint.position.z
      );
      const horizontalSecondPoint = new THREE.Vector3(
        distance.secondPoint.position.x,
        distance.secondPoint.position.y,
        distance.secondPoint.position.z
      );
      return horizontalSecondPoint.distanceTo(horizontalFirstPoint);
    },
    getMeasurementsData(measurements) {
      const distancesData = [];
      if (!this.xlsxConfigState.distances_visible) return distancesData;
      measurements.forEach((distance, index) => {
        if (distance.show) {
          const verticalComponent = this.getVerticalComponent(distance);
          const horizontalComponent = this.getHorizontalComponent(distance);
          distancesData.push({
            id: index + 1,
            name: distance.name || defaultDistanceName(index + 1, this.$t),
            length: distance.distance,
            horizontalComponent: horizontalComponent.toFixed(2),
            verticalComponent: verticalComponent.toFixed(2),
          });
        }
      });
      return distancesData;
    },
    getMeasurementAreasData(measurementAreas) {
      const measurementAreasData = [];
      if (!this.xlsxConfigState.measurements_visible)
        return measurementAreasData;
      measurementAreas.forEach((area, index) => {
        if (area.show) {
          const areaAngle = parseFloat(area?.angle);
          measurementAreasData.push({
            id: index + 1,
            name: area.name || defaultAreaName(index + 1, this.$t),
            area: area.surfaceArea,
            slope: Number.isNaN(areaAngle) ? 0 : area.angle,
            perimeter: area.lines
              .map((line) => line.label.element.distance)
              .reduce((partialSum, a) => partialSum + a, 0)
              .toFixed(2),
          });
        }
      });
      return measurementAreasData;
    },
    getPanel(area) {
      const panelTypes = [
        ...this.verticalPanelTypes,
        ...this.horizontalPanelTypes,
        ...this.customPanels,
      ];
      return panelTypes.find((panel) => panel.id === area.panelId);
    },
    getAreasData(areas) {
      const areasData = [];
      if (!this.xlsxConfigState.solar_groups_visible) return areasData;
      areas.forEach((area, index) => {
        if (area.show) {
          const panel = this.getPanel(area);
          const panelsCount = this.getAreaPanelsCount(area);
          const totalAreaOfSolarPanels = this.calculateVisiblePanelsArea(area);
          const panelDimensions = panel
            ? `${panel.size.height * 1000}mm x ${panel.size.width * 1000}mm`
            : "";
          areasData.push({
            id: index + 1,
            name: area.name || defaultPvArrayName(index + 1, this.$t),
            area: this.calculateSurfaceArea(area),
            slope: area.angle,
            panelsCount,
            panelType: panel?.name || "",
            dimension: panelDimensions,
            solarPanelsTotalArea: totalAreaOfSolarPanels,
          });
        }
      });
      return areasData;
    },
    buildXlsxPayload() {
      return {
        lang: this.$i18n.locale,
        projectId: this.project.id,
        distances: this.getMeasurementsData(this.measurements),
        measurementAreas: this.getMeasurementAreasData(this.measurementAreas),
        areas: this.getAreasData(this.areas),
      };
    },
    undo() {
      if (this.undoStack.length === 0 || this.undoLoading) return;
      this.undoLoading = true;
      const lastAction = this.undoStack.pop();
      switch (lastAction.action) {
        case "ADD_MEASUREMENT_POINT":
          this.undoAddMeasurementPoint(lastAction.measurement);
          break;
        case "ADD_POINT":
          this.undoAddPoint(
            lastAction.area,
            lastAction.point,
            lastAction.line,
            lastAction.midPoint,
            lastAction.oldTempLine,
            lastAction.oldFirstPoint,
            lastAction.newTempLine,
            lastAction.oldTempLabel,
            lastAction.newTempLabel,
            lastAction.fixedLine
          );
          break;
        case "MOVE_POINT":
          this.undoMovePoint(
            lastAction.areaType,
            lastAction.area,
            lastAction.index,
            lastAction.position
          );
          break;
        case "MOVE_MEASUREMENT_POINT":
          this.undoMoveMeasurementPoint(
            lastAction.measurement,
            lastAction.point
          );
          break;
        case "MOVE_MID_POINT":
          this.undoMoveMidPoint(
            lastAction.area,
            lastAction.index,
            lastAction.isMeasurement
          );
          break;
        case "DELETE_AREA":
          this.undoDeleteArea(lastAction.area);
          break;
        case "DELETE_MEASUREMENT_AREA":
          this.undoDeleteMeasurementArea(lastAction.area);
          break;
        case "DELETE_MEASUREMENT":
          this.undoDeleteMeasurement(lastAction.measurement);
          break;
        case "ADD_PANEL":
          this.undoAddPanel(lastAction.panel, lastAction.area);
          break;
        case "BULK_ADD_PANELS":
          this.undoBulkAddPanels(lastAction.panels, lastAction.area);
          break;
        case "MOVE_PANELS":
          this.undoMovePanels(lastAction.panels, lastAction.area);
          break;
        case "DELETE_PANELS":
          this.undoDeletePanels(lastAction.panels, lastAction.area);
          break;
        case "MERGE_POINT":
          this.undoMergePoint(
            lastAction.area,
            lastAction.points,
            lastAction.isMeasurement
          );
          break;
        default:
          null;
      }
      this.undoLoading = false;
    },
    redo() {
      if (this.redoStack.length === 0 || this.redoLoading) return;
      this.redoLoading = true;
      const lastAction = this.redoStack.pop();
      switch (lastAction.action) {
        case "ADD_MEASUREMENT_POINT":
          this.redoAddMeasurementPoint(lastAction.measurement);
          break;
        case "ADD_POINT":
          this.redoAddPoint(
            lastAction.area,
            lastAction.point,
            lastAction.line,
            lastAction.midPoint,
            lastAction.oldTempLine,
            lastAction.oldFirstPoint,
            lastAction.newTempLine,
            lastAction.oldTempLabel,
            lastAction.newTempLabel,
            lastAction.fixedLine
          );
          break;
        case "MOVE_POINT":
          this.redoMovePoint(
            lastAction.areaType,
            lastAction.area,
            lastAction.index,
            lastAction.position
          );
          break;
        case "MOVE_MEASUREMENT_POINT":
          this.redoMoveMeasurementPoint(
            lastAction.measurement,
            lastAction.point
          );
          break;
        case "MOVE_MID_POINT":
          this.redoMoveMidPoint(
            lastAction.area,
            lastAction.index,
            lastAction.points,
            lastAction.isMeasurement
          );
          break;
        case "DELETE_AREA":
          this.redoDeleteArea(lastAction.area);
          break;
        case "DELETE_MEASUREMENT_AREA":
          this.redoDeleteMeasurementArea(lastAction.area);
          break;
        case "DELETE_MEASUREMENT":
          this.redoDeleteMeasurement(lastAction.measurement);
          break;
        case "ADD_PANEL":
          this.redoAddPanel(lastAction.panel, lastAction.area);
          break;
        case "BULK_ADD_PANELS":
          this.redoBulkAddPanels(lastAction.panels, lastAction.area);
          break;
        case "MOVE_PANELS":
          this.redoMovePanels(lastAction.panels, lastAction.area);
          break;
        case "DELETE_PANELS":
          this.redoDeletePanels(lastAction.panels, lastAction.area);
          break;
        case "MERGE_POINT":
          this.redoMergePoint(
            lastAction.area,
            lastAction.points,
            lastAction.isMeasurement
          );
          break;
        default:
          null;
      }
      this.redoLoading = false;
    },
    resetRedoStack() {
      this.redoStack = [];
    },
    resetUndoStack() {
      this.undoStack = [];
    },
    fixModelRotation() {
      // this.modelObject.children[0].rotation.x = Math.PI * (-90 / 180);
      // this.project.orientation.x = -90;
    },
    calculateCameraPosition(zoomOutFactor) {
      if (!this.isFrontendOnlyDemo || this.thermal)
        return {
          xFactor: 0,
          yFactor: zoomOutFactor,
          zFactor: 0,
        };
      return this.getSampleModelCameraPosition();
    },
    getSampleModelCameraPosition() {
      switch (this.sampleModelName) {
        case "sample-1":
          return {
            xFactor: -49.7,
            yFactor: 30.9,
            zFactor: -6.8,
          };
        case "sample-2":
          return {
            xFactor: 29.4,
            yFactor: 36.4,
            zFactor: 35.7,
          };
        case "sample-3":
          return {
            xFactor: -12.4,
            yFactor: 37.3,
            zFactor: 41.9,
          };
        case "sample-4":
          return {
            xFactor: 35.5,
            yFactor: 91.2,
            zFactor: 80.6,
            xTarget: -3.5,
            yTarget: 18,
            zTarget: -17,
          };
      }
    },
    orientationUpdated() {
      this.modelObject.children[0].rotation.x = THREE.MathUtils.degToRad(
        this.project.orientation.x
      );
      this.modelObject.children[0].rotation.y = THREE.MathUtils.degToRad(
        this.project.orientation.y
      );
      this.modelObject.children[0].rotation.z = THREE.MathUtils.degToRad(
        this.project.orientation.z
      );

      this.modelObject.children[0].updateMatrixWorld(true);
    },
    async scalingUpdated(scalingValue, isInitialization) {
      this.removeMeasurements();
      if (scalingValue) {
        this.modelObject?.scale.set(scalingValue, scalingValue, scalingValue);
        if (!isInitialization) {
          await API.projects.patchProject(this.project.id, {
            solarModelScale: scalingValue,
          });
          this.project.scale = scalingValue;
        }
      }
    },
    async saveOrientation() {
      let orientationData = {
        x: this.project.orientation.x || 0,
        y: this.project.orientation.y || 0,
        z: this.project.orientation.z || 0,
      };
      const orientation = JSON.stringify(orientationData);
      await API.projects.patchProject(this.project.id, {
        solarModelOrientation: orientation,
      });
      this.project.orientation = orientationData;

      this.savedOrientation = {
        x: orientationData.x,
        y: orientationData.y,
        z: orientationData.z,
      };
    },
    resetOrientation() {
      this.project.orientation.x = this.savedOrientation.x;
      this.project.orientation.y = this.savedOrientation.y;
      this.project.orientation.z = this.savedOrientation.z;
      this.orientationUpdated();
    },
    initializeTransformControls() {
      if (!this.modelObject || this.transformControls) return;

      this.transformControls = new TransformControls(
        this.camera,
        this.renderer.domElement
      );

      this.transformControls.setMode("rotate");

      this.transformControls.setSpace("world");

      this.transformControls.setSize(1.5);

      // Rotation speed controls sensitivity - lower = less sensitive
      this.transformControls.setRotationSpeed(5);

      this.transformControls.setRotationSnap(THREE.MathUtils.degToRad(0.001));

      this.transformControls.attach(toRaw(this.modelObject.children[0]));

      // Update UI when rotation changes
      this.transformControls.addEventListener("change", () => {
        if (this.transformControls.dragging) {
          const rotation = this.modelObject.children[0].rotation;
          this.project.orientation.x = THREE.MathUtils.radToDeg(rotation.x);
          this.project.orientation.y = THREE.MathUtils.radToDeg(rotation.y);
          this.project.orientation.z = THREE.MathUtils.radToDeg(rotation.z);
        }
      });

      this.transformControls.addEventListener("dragging-changed", (event) => {
        this.cameraControls.enabled = !event.value;
      });

      this.scene.add(toRaw(this.transformControls));

      // Hide the outer ring (E/XYZE) and center free rotation sphere (XYZ)
      this.transformControls.children.forEach((child) => {
        if (child.type === "TransformControlsGizmo") {
          child.traverse((obj) => {
            if (obj.name === "E" || obj.name === "XYZE" || obj.name === "XYZ") {
              obj.visible = false;
              obj.scale.set(0, 0, 0);
              obj.layers.disable(0);
            }
          });
        }
        if (child.type === "TransformControlsPlane") {
          child.traverse((obj) => {
            if (obj.name === "E" || obj.name === "XYZE" || obj.name === "XYZ") {
              obj.visible = false;
              obj.scale.set(0, 0, 0);
              obj.layers.disable(0);
            }
          });
        }
      });

      const picker = this.transformControls.children.find(
        (child) => child.name === "picker"
      );
      if (picker) {
        picker.traverse((obj) => {
          if (obj.name === "E" || obj.name === "XYZE" || obj.name === "XYZ") {
            obj.visible = false;
            obj.scale.set(0, 0, 0);
            obj.layers.disable(0);
          }
        });
      }

      this.transformControls.enabled = false;
      this.transformControls.visible = false;
    },
    enableTransformControls() {
      if (!this.transformControls) {
        this.initializeTransformControls();
      }

      if (this.transformControls) {
        this.transformControls.enabled = true;
        this.transformControls.visible = true;
      }

      if (!this.infiniteGridHelper) {
        const size = 1000;
        const divisions = 100;
        const centerColor = 0x444444;
        const gridColor = 0x888888;

        this.infiniteGridHelper = new THREE.GridHelper(
          size,
          divisions,
          centerColor,
          gridColor
        );
        this.infiniteGridHelper.material.opacity = 0.5;
        this.infiniteGridHelper.material.transparent = true;
        this.infiniteGridHelper.material.depthWrite = false;
        this.infiniteGridHelper.renderOrder = -1;

        const modelCenter = this.modelObject.position;
        this.infiniteGridHelper.position.copy(modelCenter);

        this.scene.add(toRaw(this.infiniteGridHelper));
      } else {
        this.infiniteGridHelper.visible = true;
      }
    },
    disableTransformControls() {
      if (!this.transformControls) return;

      this.transformControls.enabled = false;
      this.transformControls.visible = false;

      // Hide infinite grid
      if (this.infiniteGridHelper) {
        this.infiniteGridHelper.visible = false;
      }
    },
    trackPageView() {
      if (
        this.galleryPointsSelected > 0 &&
        !TEST_COMPANIES.includes(this.company.id)
      ) {
        trackEvent("gallery_mode_session", {
          project_id: this.project.id,
          company_id: this.company.id,
          anonymous_user: this.anonymousUser,
          customer_type: this.currentUser.isEmployee ? "employee" : "customer",
          gallery_points_selected: this.galleryPointsSelected,
        });
      }
    },
    detectModelIntersection(event) {
      if (this.active === 1) {
        this.updatePreliminaryPointPosition(event);
      } else if (this.active === 2) {
        this.updatePreliminaryPointPositionForArea(event);
      } else if (this.active === 4) {
        this.updatePreliminaryPointPositionForMeasurementArea(event);
      } else if (this.active === 6) {
        this.updatePreliminaryPointPositionForRestrictedArea(event);
      }
    },
    displayExistingObject(element) {
      if (element.type === "MEASUREMENT") {
        this.existingMeasurementsIds.push(element.id);
        element?.position.forEach((point) => {
          this.displayMarker(element, point);
        });
        this.measurements[this.measurements.length - 1].name = element.name;
      } else if (["AREA", "SOLAR_GROUP"].includes(element.type)) {
        this.existingAreasIds.push(element.id);
        this.displayAreaPoint(element, false);
        this.areas[this.areas.length - 1].id = element.id;
        this.areas[this.areas.length - 1].show = element.show !== false;
        this.areas[this.areas.length - 1].type = element.type;
        this.areas[this.areas.length - 1].name = element.name;

        if (element.type === "SOLAR_GROUP") {
          this.useStoredSolarValues(this.areas[this.areas.length - 1], element);
        }

        this.drawPlane(this.areas[this.areas.length - 1]?.points, false);

        this.areas[this.areas.length - 1].restrictedAreas = (
          element.restricted_areas || []
        ).filter((ra) => ra.length >= 3);

        this.selectedArea = this.areas[this.areas.length - 1];
        this.processRestrictedAreas(this.areas[this.areas.length - 1]);

        this.displayIndividualPanels(this.areas[this.areas.length - 1]);
        if (element.show === false) {
          this.hideSingleArea(this.areas[this.areas.length - 1], true);
        }
      } else if (element.type === "PANEL") {
        this.displayPanel(element);
      } else if (element.type === "ANNOTATION") {
        if (
          element.norm_x != null &&
          element.norm_y != null &&
          element.image_index != null
        ) {
          this.addAnnotationMarkerToModel(
            element.image_index,
            element.norm_x,
            element.norm_y,
            element
          );
        }
      } else if (element.type === "AREA_MEASUREMENT") {
        this.existingMeasurementsIds.push(element.id);
        this.displayAreaPoint(element, true);
        this.measurementAreas[this.measurementAreas.length - 1].id = element.id;
        this.measurementAreas[this.measurementAreas.length - 1].show =
          element.show !== false;
        this.measurementAreas[this.measurementAreas.length - 1].name =
          element.name;

        this.drawMeasurementAreaPlane(
          this.measurementAreas[this.measurementAreas.length - 1]?.points,
          false
        ).catch((e) => {
          console.error(
            "FAILED AREA_MEASUREMENT id:",
            element.id,
            element.position,
            e
          );
        });
        if (
          this.measurementAreas[this.measurementAreas.length - 1].show === false
        ) {
          this.hideSingleMeasurementArea(
            this.measurementAreas[this.measurementAreas.length - 1]
          );
        }
      }
    },
    // Reads the project config into the store. The annotation fetch filters out
    // the AI damages of a project that has not opted in, so this has to have
    // landed before it runs — hence awaited rather than folded into the
    // Promise.all below.
    async loadProjectConfig() {
      try {
        const response = await API.airteam3DViewer.getProjectConfig(
          this.project.id
        );
        if (response?.data) {
          this.$store.dispatch("section/setConfig", response.data);
        }
      } catch (e) {
        console.warn("Could not load the project config:", e);
      }
    },
    async displayExistingModelObjects() {
      await this.loadProjectConfig();
      // A marker takes its visibility from this flag when it is created, so it
      // has to be restored from the config before the annotations below are
      // projected — after that only the sidebar's eye icon can change it.
      this.annotationMarkersHidden = !this.sections.config?.annotations_visible;
      const [modelObjectsResponse, annotationsResponse] = await Promise.all([
        API.airteam3DViewer.getFfSolarObjectsByProjectId(this.project.id),
        hasPermission(Permission.View_DamageAnnotations)
          ? annotationsApi.getAnnotations(this.project.id).catch((e) => {
              console.warn("Could not load annotation markers:", e);
              return { data: [] };
            })
          : { data: [] },
      ]);

      const elements = modelObjectsResponse.data;
      const annotationItems =
        annotationsResponse.data?.results ?? annotationsResponse.data ?? [];

      this.allAnnotations = annotationItems;

      elements
        .sort((a, b) => {
          const typeOrder = {
            AREA: 1,
            AREA_MEASUREMENT: 2,
            MEASUREMENT: 3,
            PANEL: 4,
          };

          return typeOrder[a.type] - typeOrder[b.type];
        })
        .forEach((element) => {
          try {
            this.displayExistingObject(element);
          } catch (e) {
            console.warn(
              `Failed to display object id=${element.id} type=${element.type}:`,
              e
            );
          }
        });

      // Only load cameras (and build frustums) when there are annotations to
      // back-project onto the model
      if (this.allAnnotations.length > 0) {
        try {
          await this.ensureCameraFrustumsForAnnotations();
        } catch (e) {
          console.warn("Could not load camera frustums for annotations:", e);
        }
      }

      this.setAllAnnotations(this.allAnnotations);

      this.hideAreas(false);
      this.setPanelsOpaque();
      if (this.anonymousUser) {
        this.checkSectionsVisibility();
      }
    },
    showSmallMeasurementMenu() {
      if (this.active === 1 || this.active === 4) {
        this.measurementMenuExpanded = false;
      }
    },
    async removeCustomPanel(event, panelToRemove) {
      const customPanels = this.customPanels.map((panel) => {
        const updatedPanel = { ...panel };
        if (panel.name === panelToRemove.name) {
          updatedPanel.show = false;
        }
        return updatedPanel;
      });
      this.setNewCustomPanels(customPanels);
      try {
        await API.airteam3DViewer.updateCustomPanel({
          id: panelToRemove.id,
          show: false,
        });
      } catch (error) {
        console.error("Failed to update custom panel:", error);
      }
      event.stopPropagation();
    },
    async loadNorthArrow() {
      const loader = new GLTFLoader();
      // v3 adds the "N" as a solid mesh sitting beyond the needle's red tip,
      // as a sibling root node — so alignArrowToProjectNorth() below swings
      // the letter with the needle, and it tilts and foreshortens in 3D with
      // the rest of the compass. See scripts/build-northarrow.py.
      loader.load("/assets/model/northarrow_v3.glb", (gltf) => {
        this.arrowHelper = gltf.scene;
        this.alignArrowToProjectNorth();
        this.cacheCompassMaterials();
        this.applyCompassTheme();
        this.arrowScene.add(this.arrowHelper);
        this.initializeNorthArrowCamera();
      });
    },
    cacheCompassMaterials() {
      // Each baked colour is kept so dark mode can restore it rather than
      // assume one shared value — the needle's south half is a different grey
      // from the letter and the ring.
      this.compassMaterials = [];
      this.arrowHelper.traverse((child) => {
        const material = child.material;
        if (!material || !COMPASS_TINTED_MATERIALS.includes(material.name)) return;
        this.compassMaterials.push({ material, baked: material.color.clone() });
      });
    },
    // These are baked into the model, so they cannot follow a CSS theme the way
    // the old DOM label did — their materials are retinted instead.
    applyCompassTheme() {
      if (!this.compassMaterials) return;
      for (const { material, baked } of this.compassMaterials) {
        if (this.isLightMode) {
          material.color.setHex(COMPASS_INK_LIGHT);
        } else if (material.name === "CompassSouth") {
          material.color.copy(baked);
        } else {
          material.color.setHex(COMPASS_INK_DARK);
        }
      }
    },
    // Rotate the arrow mesh so its red half points at the project's geographic
    // north vector. The GLTF has its red half on local -Z and the white half on
    // local +Z; world-north is project.orientation applied to model-local +Y
    // (ENU). Build a quaternion that maps -Z onto that world-north direction.
    alignArrowToProjectNorth() {
      if (!this.arrowHelper) return;
      const orientation = this.project?.orientation ?? { x: -90, y: 0, z: 0 };
      const euler = new THREE.Euler(
        THREE.MathUtils.degToRad(orientation.x || 0),
        THREE.MathUtils.degToRad(orientation.y || 0),
        THREE.MathUtils.degToRad(orientation.z || 0),
        "XYZ"
      );
      const worldNorth = new THREE.Vector3(0, 1, 0)
        .applyEuler(euler)
        .normalize();
      const tipLocal = new THREE.Vector3(0, 0, -1);
      const quat = new THREE.Quaternion().setFromUnitVectors(
        tipLocal,
        worldNorth
      );
      this.arrowHelper.setRotationFromQuaternion(quat);
    },
    updateNorthArrow() {
      this.arrowCamera.quaternion.copy(this.camera.quaternion);

      const forward = new THREE.Vector3(0, 0, -1);
      forward.applyQuaternion(this.camera.quaternion);

      const distance = COMPASS_CAMERA_DISTANCE;

      const newPosition = new THREE.Vector3()
        .copy(this.arrowHelper.position)
        .add(forward.multiplyScalar(-distance));

      this.arrowCamera.position.copy(newPosition);

      this.arrowCamera.lookAt(this.arrowHelper.position);
    },
    handleOrbitClick() {
      if (this.navigationMenu === 0) {
        // If already in orbit mode, switch back to select mode
        this.toggleActive(this.previousMode, true);
        this.previousMode = null;
        this.navigationMenu = null;
      } else {
        // Otherwise switch to orbit mode
        this.toggleNavigation(0);
      }
    },
    handlePanClick() {
      if (this.navigationMenu === 1) {
        // If already in pan mode, switch back to select mode
        this.toggleActive(this.previousMode, true);
        this.previousMode = null;
        this.navigationMenu = null;
      } else {
        // Otherwise switch to pan mode
        this.toggleNavigation(1);
      }
    },
    handleGalleryClick() {
      if (this.active === 5) {
        // if already in gallery mode, do nothing
        return;
      } else {
        // Otherwise switch to gallery mode
        this.toggleActive(5);
      }
    },
    async checkSectionsVisibility() {
      const config = await API.airteam3DViewer.getProjectConfig(
        this.project.id
      );
      if (!config.data.distances_visible) {
        this.hideMeasurements();
      }
      if (!config.data.measurements_visible) {
        this.hideAllMeasurementAreas();
      }

      if (!config.data.solar_groups_visible) {
        this.hideAreas();
      }
    },
    toggleFullScreenImageActive(value) {
      this.fullScreenImageActive = value;
    },
    openAnnotationInGallery(cameraIndex, annotationId, imageId = null) {
      if (this.active !== 5) {
        // Outside the gallery a damage opens on its own photo alone. Switching
        // the whole viewer into gallery mode to show one image dropped the mode
        // the user was working in and put a photo list in front of them that
        // they had not asked for.
        this.openDamageImage(annotationId, imageId);
        return;
      }
      this.annotationOpenRequest = {
        cameraIndex,
        annotationId,
        imageId,
        ts: Date.now(),
      };
    },

    // Fetches the damage's own photo from AFP by the image_id on the annotation
    // and shows it in the standalone image view.
    async openDamageImage(annotationId, imageId) {
      if (imageId == null) {
        console.warn(`Annotation ${annotationId} has no photo to open.`);
        return;
      }
      this.damageHighlightId = annotationId;
      this.damageImageLoading = true;
      this.toggleFullScreenImageActive(true);
      try {
        const { data } = await API.projectFileCategories.getProjectImage(
          imageId
        );
        // The annotation is matched to its photo by pk, so never let the
        // detail response decide whether that field is there.
        this.damageImage = { ...data, pk: data?.pk ?? Number(imageId) };
      } catch (e) {
        console.error(
          `Could not load the photo of annotation ${annotationId}:`,
          e
        );
        this.closeDamageImage();
      } finally {
        this.damageImageLoading = false;
      }
    },

    closeDamageImage() {
      this.damageImage = null;
      this.damageHighlightId = null;
      this.damageImageLoading = false;
      this.toggleFullScreenImageActive(false);
    },

    // The standalone view has no gallery to refresh the list for it. Markers
    // are added and removed by the view itself, so this only re-reads the data
    // the sidebar renders; projectableAnnotations skips ids already placed.
    async refreshDamageAnnotations() {
      try {
        const response = await annotationsApi.getAnnotations(this.project.id);
        const items = response.data?.results ?? response.data ?? [];
        this.setAllAnnotations(items);
      } catch (e) {
        console.warn("Could not refresh the annotations:", e);
      }
    },
    handlePageShow(event) {
      this.checkAuthenticationStatus();
    },
    checkAuthenticationStatus() {
      if (!this.isFrontendOnlyDemo && !this.anonymousUser) {
        const key = `${isProduction ? "" : "staging"}loggedoutstatus`;
        const loggedOutStatus = getCookie(key);
        if (loggedOutStatus) {
          authZitadelMethods.noCurrentProject();
          return false;
        }
      }
      return true;
    },
  },
};
</script>

<script setup>
import { useDownloadAllDialog } from "@/composables/useDownloadAllDialog";

const { showDownloadAllDialog } = useDownloadAllDialog();
</script>

<style scoped lang="scss">
@use "@/scss/_typography" as typography;
@use "@/scss/_colors" as colors;

.project-panel {
  &__summary {
    @include typography.getFont($font-name: subtitle1);
    color: white;
  }
  &__field {
    @include typography.getFont($font-name: subtitle3);
    font-size: 12px;
    display: flex;
    justify-content: space-between;

    &--label {
      color: white;
      font-weight: 400;
    }

    &--value {
      color: white;
      font-weight: 200;
    }
  }
  &__settings {
    justify-content: space-between;
  }
  &__title {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  &__solar-card {
    border-radius: 8px;
    border: 0;
    background: colors.$GREB50;

    &-header {
      border-radius: 8px;
    }

    &-content {
      background: colors.$GREB50;
    }
  }
}

.breadcrumbs {
  &__container {
    background-color: var(--color-dark-bg-secondary);
    color: white;
  }

  &__last {
    font-weight: bold;
    color: white !important;
  }
}

.new-sidebar-layout {
  background: var(--color-dark-bg-primary);
  color: white;
  height: 100vh;

  p {
    color: white;
  }
  span {
    color: white;
  }
}
.sidebar-bottom {
  max-height: 44rem;
  overflow-y: scroll;
  scrollbar-width: none;
}

.sidebar-bottom--ai {
  max-height: calc(100vh - 150px);
  overflow-y: hidden;
  display: flex;
  flex-direction: column;

  .ai-design-section {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    padding-bottom: 12px;
  }
}

hr.project-panel__line {
  margin-left: -24px;
  margin-right: -24px;
}

.close-sidebar {
  margin-right: -35px;

  div {
    background-color: var(--color-white);
    z-index: 9999;
    position: relative;
    border: solid 1px var(--color-border-light);
    border-radius: 10px;
  }
}

.open-sidebar {
  position: fixed;
  top: 70px;
  z-index: 9999;

  div {
    background-color: var(--color-white);
    z-index: 9999;
    position: relative;
    border: solid 1px var(--color-border-light);
    border-radius: 10px;
  }
}

.panels__toolbar {
  background: colors.$GREB50;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  padding: 10px 0px;
  z-index: 15 !important;

  &--small {
    padding: 5px;
  }

  &--area {
    padding-right: 0;
  }

  &-text {
    font-family: "Roboto";
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 14px;
    text-align: center;
    letter-spacing: 0.05em;
    flex-wrap: nowrap;

    &--small {
      height: 35px;
      display: flex;
      align-items: center;
      padding: 5px 10px;
      border-radius: 5px;
      background: var(--color-dark-bg-primary);
    }

    &--area {
      margin-left: 0;
    }
  }
  &-trash {
    margin-left: 15px;
    margin-top: 5px;
  }
  &-container {
    max-height: 50vh;
    overflow: scroll;
    margin: 0;
  }
  &-custom-button {
    width: calc(100% - 24px);
  }
  &-submenu {
    &__title {
      @include typography.getFont($font-name: subtitle1);
      color: black;
    }
    &__header {
      font-family: "Roboto";
      font-style: normal;
      font-weight: 500;
      font-size: 16px;
      line-height: 14px;
      text-align: center;
      letter-spacing: 0.05em;

      &--small {
        font-size: 13px;
      }
    }
    &__subheader {
      font-family: "Roboto";
      font-style: normal;
      font-weight: 500;
      font-size: 12px;
      line-height: 14px;
      text-align: center;
      letter-spacing: 0.05em;
    }
    &__button {
      margin-left: 0;
      display: flex;
      justify-content: center;

      :deep(button) {
        width: 142px;
      }
    }
  }
}

.main-menu {
  background: var(--color-dark-bg-primary);
  // Spacing lives on each tool, not the column: 7px either side and 12px
  // above and below the 36x36 icon tile (50px wide, 60px per tool), with
  // dividers spanning the full width between tools.
  padding: 0;

  > .panels__toolbar-text {
    padding: 12px 7px;
  }

  // Dividers fill the column without setting its width: a percentage width
  // on an image still counts its natural 60px when the toolbar sizes itself
  // to its content, so width 0 + min-width 100% leaves the icons in charge.
  > img {
    display: block;
    width: 0;
    min-width: 100%;
  }

  &--small {
    background: var(--color-dark-bg-tertiary);
  }
}

.panel--selected {
  background: colors.$HONE200;
}

.position__right {
  position: absolute;
  right: 0;
}

.mt-auto {
  margin-top: auto;
}

.airteam-viewer {
  font-family: "Roboto", sans-serif;
  overflow: hidden;
  margin: 0;
  padding: 0px;
  background: hsl(0, 0%, 30%);

  :deep(canvas) {
    width: 100% !important;
  }
}

.ai-preview-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: hsl(0, 0%, 10%);
  z-index: 5;
  pointer-events: none;
}

.col-p5 {
  width: 2%;
  text-align: center;
  background: var(--color-dark-bg-primary);
}

.col-100 {
  position: relative;
  width: 100%;
}

.col-115 {
  position: relative;
  width: 98%;
}

.position4,
.position5,
.position6,
.position55 {
  position: absolute;
  color: #fff;
  z-index: 15 !important;
}

.add-panels-menu {
  width: 240px;
}

.position4 {
  position: fixed;
  bottom: 14px;
  margin-left: 24px;
  text-align: left;
}

.position5 {
  z-index: 6000 !important;
  top: 50vh;
  transform: translateY(-50%);
  text-align: left;

  &--disabled {
    z-index: 1 !important;
  }
}

.position55 {
  top: 40vh;
  left: 60px;
  transform: translateY(-50%);
  text-align: left;
}

.measurement-menu {
  position: absolute;
  color: #fff;
  z-index: 15 !important;
  text-align: left;
}

.position6 {
  top: 50%;
  transform: translateY(-50%);
  margin-left: 6.7rem;
  text-align: left;
}

.help-button {
  border: none;
  display: block;
  cursor: pointer;
  outline: none;
  // 26px so .nav-button's 6px padding gives a 38x38 pill, the same as the orbit
  // and pan buttons beside it. Close to the ~27px the glyph rendered at inside
  // its old baked-in chip.
  width: 26px;
  height: 26px;
}

.airteam-logo {
  margin-top: -12px;
}

a {
  color: var(--color-map-green);
}

.button {
  border: none;
  padding: 10px 10px;
  text-align: center;
  text-decoration: none;
  display: inline-block;
  font-size: 16px;
  margin: 2px 2px;
  cursor: pointer;
  outline: none;
}

.add-panels {
  width: 100%;
}

.export-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  /* Pinned to the sidebar bottom (like the old report button) so a long
     distances/annotations list never pushes it out of view. */
  position: absolute;
  bottom: 40px;
  width: calc(100% - 48px);
  padding: 10px 16px;
  background: var(--color-gree300);
  color: var(--color-on-accent);
  border: none;
  border-radius: 5px;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: filter 0.12s ease, transform 0.12s ease;
  &:hover {
    filter: brightness(1.06);
    transform: translateY(-1px);
  }
  &:active {
    transform: translateY(0);
  }
}

.add-panels-text {
  margin: 4px 0px;
  color: var(--color-greb400);
  letter-spacing: 1.5px;
  font-size: 8px;
}

.dark {
  background-color: var(--color-viewer-green-darker);
  color: white;
}

.Cclay {
  background-color: var(--color-mode-area);
  color: white;
}

.Cblue {
  background-color: var(--color-mode-measure);
  color: white;
}

.Cgreen {
  background-color: var(--color-mode-solar);
  color: black;
  border-radius: 25px;
}

.Cgrey {
  background-color: var(--color-mode-inactive);
  color: black;
}

.Cred {
  background-color: var(--color-mode-restricted);
  color: white;
}

.green-dot-cursor {
  cursor: url("@/assets/icons/green-dot.svg"), auto;
}

.panel-text {
  color: var(--color-text-muted);
  font-size: 10px;

  &--active {
    color: var(--color-primary);
  }
}

#draggable-container {
  position: absolute;
  z-index: 9;
}
#draggable-header {
  z-index: 10;
  cursor: grab;
}

.close-row {
  margin-top: -15px;
}

.close-menu {
  cursor: pointer;
}

.move-menu {
  position: absolute;
  left: 0;
  margin-left: -40px;
  margin-top: -25px;
}

.position0 {
  position: absolute;
  display: flex;
  top: 1.4em;
  left: 1em;
  text-align: right;
  margin-left: 50px;
}

.undo-redo {
  background: rgba(59, 60, 61, 1);
  border-radius: 8px;
  z-index: 15 !important;
  display: inline-block;
  width: 50px;
  height: 45px;
  text-align: center;
  border: 1px solid rgba(39, 41, 44, 1);
}

// Top-right action bar, attached to the top and right edges of the viewer, so
// only its bottom-left corner is rounded.
.screenshot-bg {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: #3b3c3d;
  border-radius: 0 0 0 4px;
}

.action-tile {
  display: block;
  padding: 0;
  border: 0;
  background: none;
  line-height: 0;
  border-radius: 7px;

  // Scaled to the toolbar icons' 36px height; the width follows each tile's
  // own proportions (37x30 and 72x30 as drawn).
  svg {
    display: block;
    width: auto;
    height: 36px;
  }

  &:focus,
  &:focus-visible {
    outline: none;
  }
}

.position1 {
  position: absolute;
  background-color: rgba(59, 60, 61, 1);
  color: colors.$EMER500;
  right: 0;
  border-radius: 0 0 0 8px;
  overflow: visible;
  text-align: right;
  z-index: 150 !important;
}


.position2 {
  position: absolute;
  right: 0;
  top: 60px;
  padding: 0 20px;
  text-align: right;
  z-index: 15 !important;
}

.position9 {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-40%);
  background: var(--color-dark-bg-primary);
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  z-index: 15 !important;
  display: flex;
  align-items: center;

  > span {
    display: inline-flex;
    align-items: center;
  }

  img {
    vertical-align: middle;
  }

  &--sample {
    transform: translateX(-40%);
  }
}


// The canvas is wider than the disc so the model's "N" has somewhere to sit
// outside it, and the extra width is transparent. $compass-inset is how far the
// disc's edge is inset from the element's, and it is subtracted back out of the
// vertical offset below so the disc sits 64px up, where it always did.
$compass-canvas: 114px;
$compass-disc: 74px;
$compass-inset: ($compass-canvas - $compass-disc) * 0.5;

.position11 {
  position: fixed;
  bottom: 64px - $compass-inset;
  // Horizontal offset is of the *element*, not the disc: the letter needs
  // $compass-inset of clear room outside the disc, so the disc ends up
  // ($compass-inset + this) from the viewport edge. Cannot go below 0 without
  // clipping the letter when north points due east.
  right: 8px;
  // Sized explicitly because `.airteam-viewer :deep(canvas)` forces every
  // canvas in the viewer to `width: 100% !important`. Left to shrink-wrap, this
  // box resolved to the compass canvas's *backing store* size instead — which
  // is the CSS size times the device pixel ratio, so it came out double on a
  // retina screen.
  width: $compass-canvas;
  height: $compass-canvas;
  font-size: 12px;
  transition: right 0.15s ease;
  // Nothing in here should swallow clicks meant for the model behind it.
  pointer-events: none;

  // The visible disc. Drawn behind the canvas (same stacking level, earlier in
  // paint order) and sized independently of it, so the canvas can extend past
  // it to hold the letter without the tint spreading into that margin.
  &::before {
    content: "";
    position: absolute;
    left: 50%;
    top: 50%;
    width: $compass-disc;
    height: $compass-disc;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    background-color: rgba(238, 238, 238, 0.1);
  }

  // When the SolarSidebar is open, shift the arrow left so it stays visible.
  // Widths mirror SolarSidebar's `.main-container` breakpoints (270/240/200).
  &--shifted {
    right: 270px + 16px - $compass-inset;

    @media (max-width: 560px) {
      right: 200px + 16px - $compass-inset;
    }
  }

  // Fill the box above exactly. Both dimensions are forced because the viewer
  // sets `height: 100%` inline on whichever canvas it finds first, and the
  // `width: 100% !important` rule elsewhere in this file would win regardless.
  :deep(canvas) {
    display: block;
    width: 100% !important;
    height: 100% !important;
  }
}

// Tinted a little more strongly on the light theme so the needle's white half
// stays visible against it. On the disc only — on the container it would paint
// the whole square canvas box.
.light-mode .position11::before {
  background-color: rgba(38, 65, 90, 0.18);
}


// Above every other layer in the app: Element Plus overlays are forced to
// 60001 in style.scss, Quasar tops out at 9998, and the iframe auth guard sits
// at 99999. One below ShareModelModal's own z-index, so the dialog stays on top
// of its own backdrop.
.share-model-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 100000;
}

.cursor-disabled {
  cursor: not-allowed;
  color: colors.$GREY500;
}


.height-75 {
  height: 75px;
}

.height-250 {
  height: 250px;
}

.menu-icon {
  // Block, so an inline SVG leaves no baseline gap under it, centred with auto
  // margins (text-align doesn't centre a block). The icons are drawn as 48x48
  // tiles and shown at 36x36.
  display: block;
  width: 36px;
  height: 36px;
  margin: 0 auto;
  padding: 0px;

  &--small {
    width: 18px;
  }
}


.ai-design-section {
  background: var(--color-dark-bg-secondary);
  border-radius: 10px;
  padding: 0 10px;
  margin-top: 1rem;
}

.undo-arrow {
  width: 35px;
  height: 20px;
  position: relative;
  top: 40%;
  transform: translateY(-40%);
}

.orientation-values {
  display: flex;
  flex-direction: row;
}

.orientation-input-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.orientation-label {
  color: white;
  font-weight: 500;
  font-size: 13px;
  margin: 0;
  min-width: 20px;
}

.orientation-input-field {
  border-radius: 2px;
  width: 95px;
  color: white;
}

.orientation-input-field :deep(.el-input__wrapper) {
  position: relative;
  display: inline-block;
  box-shadow: none;
  background-color: var(--color-dark-bg-primary);
  padding-left: 8px;
  padding-right: 39px;
}

.orientation-input-field :deep(.el-input__wrapper)::after {
  content: "°";
  position: absolute;
  top: 50%;
  right: 34px;
  transform: translateY(-50%);
  font-size: 14px;
  color: white;
  pointer-events: none;
}
.orientation-input-field :deep(.el-input__inner) {
  color: white;
  text-align: center;
  border-radius: 2px !important;
  cursor: text;
}

.orientation-input-field :deep(.el-input-number__decrease) {
  box-shadow: none;
  background-color: var(--color-dark-bg-primary);
  border-color: var(--color-dark-bg-secondary) !important;
  color: white;
}

.orientation-input-field :deep(.el-input-number__increase) {
  box-shadow: none;
  background-color: var(--color-dark-bg-primary);
  border-color: var(--color-dark-bg-secondary) !important;
  color: white;
}

.reset-orientation-link {
  color: var(--color-gree300) !important;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: var(--color-map-green);
    text-decoration: none;
  }
}

.orientation-divider {
  border: none;
  border-top: 1px solid var(--color-dark-bg-secondary);
  margin: 16px 0;
}

.camera-toggle-btn {
  width: 100%;
  font-size: 12px;
}

.scale-input-field {
  width: 100%;
  padding: 8px 12px;
  background-color: var(--color-dark-bg-primary);
  color: white;
  border: 1px solid var(--color-dark-bg-secondary);
  border-radius: 4px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: var(--color-gree300);
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    appearance: textfield;
    -moz-appearance: textfield;
  }
}

.ff3d-btn::before {
  border-color: colors.$GREY300 !important;
}

.menu-text {
  font-weight: normal;
}

.menu-text--active {
  color: var(--color-gree300);
}

.wrapper {
  background: black;
  height: 100vh;
}

.progress-container {
  color: var(--color-grey50);
  text-align: center;
  font-size: 24px;
  font-weight: 700;
}

.progress-hint {
  font-size: 18px;
  font-weight: normal;
}

.thumbnail {
  margin-bottom: 8px;
  width: 500px;
}

#imageContainer {
  z-index: 1000;
  position: absolute;
  display: none;
  pointer-events: none; /* This ensures the container doesn't interfere with mouse events */
}

#zoom-plus,
#zoom-minus {
  position: absolute;
  width: 50px;
  height: 50px;
}

#zoom-plus {
  bottom: 100%; /* Position it above the container's top edge */
  left: 50%; /* Center it horizontally */
  transform: translateX(-50%); /* Adjust for image's own width */
}

#zoom-minus {
  top: 20px; /* Position it below the container's bottom edge */
  left: 50%; /* Center it horizontally */
  transform: translateX(-50%); /* Adjust for image's own width */
}

@media only screen and (max-width: 560px) {
  .hide-mobile {
    display: none;
  }

  .position9 {
    left: 10%;
  }

  .position1 {
    padding: 10px 20px;
  }

  .project-panel {
    z-index: 100;
  }

  .close-sidebar {
    margin-right: 0;
  }

  .open-sidebar {
    margin-left: 20px;
  }
}

.view-plane-button {
  background-color: rgba(59, 60, 61, 1);
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  min-width: 50px;
  text-align: center;
  border: 1px solid rgba(39, 41, 44, 1);
  transition: background-color 0.2s, color 0.2s;

  &:hover {
    background-color: rgba(80, 82, 85, 1);
  }

  &--active {
    background-color: var(--color-gree300);
    color: black;
    border-color: var(--color-gree300);
  }
}
</style>

<style lang="scss">
.share-title {
  font-weight: bold;
  font-size: 13px;
  color: white;
  padding-left: 10px;
}
.share-message-body {
  font-size: 13px;
  color: white;
  padding-left: 30px;
  padding-right: 30px;
}
.share-message-body .valid {
  font-weight: bold;
}
.share-close-button {
  span {
    font-weight: bold;
    font-size: 14px;
    color: var(--color-primary) !important;
  }
}
.el-overlay {
  z-index: 60001 !important;
}
@media (max-width: 1600px) {
  .q-item__label {
    font-size: 12px;
  }
  .q-field__control.relative-position.row.no-wrap {
    padding: 0 3px;
    input {
      text-align: end;
    }
  }
  span {
    font-size: 12px;
  }
}

@media (max-width: 1450px) {
  .q-field__control.relative-position.row.no-wrap {
    padding: 0 1px;
    input {
      text-align: end;
    }
  }
}
@media (max-height: 850px) {
  .new-sidebar-layout {
    .sidebar-bottom {
      max-height: 30rem;
    }
  }
}
.nav-button {
  border-radius: 8px;
  padding: 6px 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &--active {
    background-color: var(--color-gree300);
    img,
    svg {
      filter: brightness(0);
    }
  }
}

body.within-iframe .new-sidebar-layout .sidebar-bottom--iframe {
  height: calc(100vh - 270px);
  flex: none;
}

/* Tighten the spacing around the employee-only Toggle FPS row. The shared
   ToggleSwitch keeps its default padding in the gallery; here we trim it. */
/* Negative margins pull the divider toward Scale Model (above) and the Solar
   section up (below) — the real gaps come from Scale Model's 12px margin-bottom
   and the section-wrapper's 16px margin-top, which we don't want to change
   globally. Scoped to the employee-only Toggle FPS row only. */
.project-panel__line--tight {
  margin-top: -8px;
  margin-bottom: 2px;
}

.fps-toggle {
  margin-bottom: -9px;
}

.fps-toggle .toggle-switch-row {
  padding-top: 1px;
  padding-bottom: 1px;
}
</style>
